import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import customerService from "../services/customerService";
import orderService from "../services/orderService";
import paymentService from "../services/paymentService";
import productService from "../services/productService"; // productService import edildi
import { toast } from "react-toastify";
import { PaymentMethod, PaymentMethodMapping } from "../constants/enums";
import AddressModal from "./AddressModal";
import CustomModal from "./CustomModal";
import LoadingSpinner from "./common/LoadingSpinner";
import {
  FaEdit,
  FaUniversity,
  FaMoneyBillWave,
  FaCreditCard,
  FaChevronUp,
  FaChevronDown,
  FaPlus,
  FaLock,
} from "react-icons/fa";
import { formatQuantityWithUnit } from "../utils/unitHelpers";
import "../styles/checkout.css";

const Checkout = () => {
  const [selectedPayment, setSelectedPayment] = useState(
    PaymentMethod.CREDIT_CARD
  );
  const { cart, getCartTotalPrice, clearCart } = useCart();
  const imageApiUrl = (
    process.env.REACT_APP_IMAGE_API_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/api\/?$/, "");
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // useLocation hook'unu ekleyelim
  const [loading, setLoading] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [hasAddress, setHasAddress] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [orderNotes, setOrderNotes] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addressModalType, setAddressModalType] = useState("shipping");
  const [selectedAddressForEdit, setSelectedAddressForEdit] = useState(null);
  const [useSameAddressForBilling, setUseSameAddressForBilling] =
    useState(true);
  const [selectedBillingAddressId, setSelectedBillingAddressId] =
    useState(null);
  const [billingAddresses, setBillingAddresses] = useState([]);
  // Kredi kartı bilgileri için state
  const [cardInfo, setCardInfo] = useState({
    cardNumber: "",
    cardHolderName: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",
  });
  // Taksit seçenekleri için state - geçici olarak devre dışı
  // const [installmentOptions, setInstallmentOptions] = useState([]);
  // const [selectedInstallment, setSelectedInstallment] = useState(1); // Default: tek çekim
  // const [fetchingInstallments, setFetchingInstallments] = useState(false);
  // BIN detayları için state
  // const [binDetails, setBinDetails] = useState(null);

  // Taksit seçenekleri geçici olarak sabit değerler
  const installmentOptions = [];
  const selectedInstallment = 1;
  const fetchingInstallments = false;
  const binDetails = null;
  // Accordion başlangıç durumları için state'ler
  const [accordionStates, setAccordionStates] = useState({
    cartItems: false, // Sepet ürünleri kapalı
    address: true, // Adres açık
    orderNote: true, // Sipariş notu açık
    payment: true, // Ödeme açık
    terms: true, // Sözleşmeler açık
  });

  // PayTR 3D Secure Page için state'ler
  const [show3DSecurePage, setShow3DSecurePage] = useState(false);
  const [threeDSecureHtmlContent, setThreeDSecureHtmlContent] = useState(null);


  // Client IP almak için basit fonksiyon
  const getClientIp = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.warn("Client IP alınamadı:", error);
      return null; // Backend'de fallback IP kullanılacak
    }
  };

  // Sözleşme modalı için state'ler
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsModalContent, setTermsModalContent] = useState({
    title: "",
    content: "",
  });

  // Misafir ödeme için state'ler
  const [isGuestCheckout, setIsGuestCheckout] = useState(false);
  const [guestInfo, setGuestInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    identityNumber: "", // TC Kimlik No eklendi
    address: "",
    city: "",
    district: "",
    postCode: "",
    // Misafir için fatura bilgileri (opsiyonel, şimdilik teslimatla aynı varsayılabilir)
    billingFirstName: "",
    billingLastName: "",
    billingAddress: "",
    billingCity: "",
    billingDistrict: "",
    billingPostCode: "",
    billingPhone: "", // Gerekirse eklenebilir
    billingEmail: "", // Gerekirse eklenebilir
  });
  const [guestWantsDifferentBilling, setGuestWantsDifferentBilling] =
    useState(false);

  // Sepet ürün detayları için state'ler
  const [detailedCartItems, setDetailedCartItems] = useState([]);
  const [cartDetailsLoading, setCartDetailsLoading] = useState(true);
  
  // Kargo fiyatı için state
  const [shippingCost, setShippingCost] = useState(0);
  
  // Kargo fiyatını hesapla
  const calculateShippingCost = useCallback(async () => {
    try {
      // Sepet ürünlerinin toplam ağırlığını hesapla
      let totalWeight = 0;
      
      // Normal ürünler - detailedCartItems varsa onu kullan, yoksa cart.items'dan weight bilgisini al
      if (detailedCartItems && detailedCartItems.length > 0) {
        totalWeight += detailedCartItems.reduce((total, item) => {
          // SaleType kontrolü yap
          const saleType = item.saleType !== undefined 
            ? item.saleType 
            : (item.weightInGrams ? 1 : 0);
          
          let itemWeight = 0;
          if (saleType === 1) {
            // Kilogram bazlı ürünler için gerçek ağırlık (weightInGrams'ı kg'a çevir)
            // Kilogram bazlıda quantity her zaman 1'dir
            itemWeight = (item.weightInGrams || item.weightGrams || 0) / 1000;
          } else {
            // Adet bazlı ürünler için 1 adet = 1kg
            itemWeight = 1 * item.quantity;
          }
          
          return total + itemWeight;
        }, 0);
      }
      
      // Custom paketler (eğer varsa)
      if (cart && cart.customPackages && cart.customPackages.length > 0) {
        totalWeight += cart.customPackages.reduce((total, pkg) => {
          return total + pkg.items.reduce((pkgTotal, item) => {
            return pkgTotal + (item.weightGrams || 0) * item.quantity / 1000; // gram'ı kg'a çevir
          }, 0);
        }, 0);
      }
      
      if (totalWeight > 0) {
        const response = await orderService.calculateShippingCost(totalWeight);
        setShippingCost(response.cost || 0);
      } else {
        setShippingCost(0);
      }
    } catch (error) {
      console.error('Kargo fiyatı hesaplanırken hata:', error);
      setShippingCost(0);
    }
  }, [detailedCartItems, cart]);

  // Component başına bir stil objesi tanımlıyoruz
  const accordionHeaderStyles = {
    base: {
      cursor: "pointer",
      padding: "15px",
      position: "relative",
      transition: "all 0.2s ease",
    },
    open: {
      backgroundColor: "white",
      borderRadius: "8px 8px 0 0",
    },
    closed: {
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    },
    hover: {
      backgroundColor: "#f0f0f0",
    },
  };

  // State'ler için hover durumunu takip edeceğiz
  const [hoverStates, setHoverStates] = useState({
    cartItems: false,
    address: false,
    orderNote: false,
    payment: false,
    terms: false,
  });

  const rawTotalPrice = getCartTotalPrice(); // Sepetin ham toplamı

  let finalDisplayPrice = rawTotalPrice + shippingCost; // Kargo fiyatı dahil toplam

  // Taksit hesaplama geçici olarak devre dışı
  // if (
  //   selectedPayment === PaymentMethod.CREDIT_CARD &&
  //   selectedInstallment > 1 &&
  //   installmentOptions.length > 0
  // ) {
  //   const currentInstallmentPlan = installmentOptions.find(
  //     (opt) => opt.installment === selectedInstallment
  //   );
  //   if (currentInstallmentPlan && currentInstallmentPlan.amount != null) {
  //     finalDisplayPrice = currentInstallmentPlan.amount * selectedInstallment;
  //   } else if (
  //     currentInstallmentPlan &&
  //     currentInstallmentPlan.totalPrice != null
  //   ) {
  //     finalDisplayPrice = currentInstallmentPlan.totalPrice;
  //   }
  //   // Eğer uygun bir taksit planı bulunamazsa veya amount/totalPrice null ise,
  //   // finalDisplayPrice rawTotalPrice olarak kalır, bu da bir fallback görevi görür.
  // }

  // Hover olayları için fonksiyonlar
  const handleMouseEnter = (section) => {
    setHoverStates((prev) => ({
      ...prev,
      [section]: true,
    }));
  };

  const handleMouseLeave = (section) => {
    setHoverStates((prev) => ({
      ...prev,
      [section]: false,
    }));
  };

  // Stil oluşturucu yardımcı fonksiyon
  const getHeaderStyle = (section) => {
    const isOpen = accordionStates[section];
    const isHover = hoverStates[section];

    return {
      ...accordionHeaderStyles.base,
      ...(isOpen ? accordionHeaderStyles.open : accordionHeaderStyles.closed),
      ...(isHover && !isOpen ? accordionHeaderStyles.hover : {}),
    };
  };

  // Accordion durumunu değiştirme fonksiyonu
  const toggleAccordion = (section) => {
    setAccordionStates((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Sayfa yüklendiğinde müşteri adreslerini çek
  useEffect(() => {
    const fetchAddressesInternal = async () => {
      try {
        // Kullanıcı giriş yapmamışsa API'ye istek gönderme
        if (!isAuthenticated()) return;

        setLoading(true);
        const response = await customerService.getCustomerAddresses();

        if (response.succeeded) {
          // Teslimat adresleri
          const shippingAddresses =
            response.addresses?.filter((a) => a.addressType === 1) || [];
          setAddresses(shippingAddresses);

          // Fatura adresleri
          const billingAddrs =
            response.addresses?.filter((a) => a.addressType === 2) || [];
          setBillingAddresses(billingAddrs);

          // Varsayılan adresi seç, yoksa ilk adresi
          const defaultAddress = shippingAddresses.find((a) => a.isDefault);
          if (defaultAddress) {
            setSelectedAddressId(defaultAddress.id);
            setShippingAddress({
              firstName: defaultAddress.firstName || "",
              lastName: defaultAddress.lastName || "",
              company: "",
              country: "Türkiye",
              address: defaultAddress.address || "",
              apartment: "",
              city: defaultAddress.province || "",
              district: defaultAddress.district || "",
              postCode: defaultAddress.postalCode || "",
              phone: defaultAddress.phone || "",
              email: user?.email || "",
            });
          } else if (shippingAddresses.length > 0) {
            setSelectedAddressId(shippingAddresses[0].id);
            setShippingAddress({
              firstName: shippingAddresses[0].firstName || "",
              lastName: shippingAddresses[0].lastName || "",
              company: "",
              country: "Türkiye",
              address: shippingAddresses[0].address || "",
              apartment: "",
              city: shippingAddresses[0].province || "",
              district: shippingAddresses[0].district || "",
              postCode: shippingAddresses[0].postalCode || "",
              phone: shippingAddresses[0].phone || "",
              email: user?.email || "",
            });
          }

          // Varsayılan fatura adresi seç, yoksa ilk adresi
          const defaultBillingAddress = billingAddrs.find((a) => a.isDefault);
          if (defaultBillingAddress) {
            setSelectedBillingAddressId(defaultBillingAddress.id);
          } else if (billingAddrs.length > 0) {
            setSelectedBillingAddressId(billingAddrs[0].id);
          }

          setHasAddress(shippingAddresses.length > 0);
        } else {
          console.error("Adresler alınamadı:", response.message);
        }
      } catch (error) {
        console.error("Adresler yüklenirken hata:", error);
        toast.error("Adresleriniz yüklenemedi. Lütfen tekrar deneyin.");
      } finally {
        setLoading(false);
      }
    };

    // Eğer kullanıcı giriş yapmışsa adresleri çek
    if (isAuthenticated()) {
      fetchAddressesInternal();
    }
  }, [isAuthenticated, user]);

  // Misafir olarak devam et state'ini kontrol et
  useEffect(() => {
    if (location.state?.proceedAsGuest && !isGuestCheckout) {
      setIsGuestCheckout(true);
      // Adres accordion'unu aç (kullanıcı misafir formunu direkt görsün diye)
      if (!accordionStates.address) {
        // toggleAccordion referansının stabil olduğundan emin olunmalı veya useCallback ile sarmalanmalı.
        // Şimdilik doğrudan çağırıyoruz.
        toggleAccordion("address");
      }
      // State'i TEMİZLEMİYORUZ. Bu sayede ProtectedRoute tekrar kontrol ettiğinde misafir olduğunu anlar.
      // navigate(location.pathname, { replace: true, state: {} }); // BU SATIR KALDIRILDI
    }
  }, [
    location.state,
    isGuestCheckout,
    setIsGuestCheckout,
    navigate,
    accordionStates.address,
    toggleAccordion,
  ]);

  // Sepetteki ürün detaylarını API'den çekmek için useEffect
  useEffect(() => {
    const fetchCartProductDetails = async () => {
      if (cart && cart.items && cart.items.length > 0) {
        setCartDetailsLoading(true);
        try {
          const promises = cart.items.map(async (item) => {
            const productDetailsResponse = await productService.getProductById(
              item.productId
            );
            if (
              productDetailsResponse.succeeded &&
              productDetailsResponse.product
            ) {
              return {
                ...item, // Sepetteki orijinal miktar, weightInGrams, weightGrams ve diğer bilgiler korunur
                productId: productDetailsResponse.product.id,
                productName: productDetailsResponse.product.name, // API'den gelen güncel isim
                // API yanıtında productImageUrl alanı varsa onu kullan, yoksa item.productImageUrl'e geri dön
                productImageUrl:
                  productDetailsResponse.product.imageUrl ||
                  productDetailsResponse.product.image ||
                  item.productImageUrl,
                price: productDetailsResponse.product.price, // API'den gelen güncel birim fiyat
                discountedPrice: productDetailsResponse.product.discountedPrice, // API'den gelen güncel indirimli birim fiyat
                weight: productDetailsResponse.product.weight || 0, // Ağırlık bilgisi
                category1:
                  productDetailsResponse.product.category?.name || "Diğer", // Ana Kategori
                category2:
                  productDetailsResponse.product.subCategory?.name ||
                  "Diğer Alt", // Alt Kategori (varsa)
              };
            } else {
              // Ürün detayı alınamazsa, mevcut bilgilerle devam et (veya hata logla)
              console.warn(
                `Ürün detayı alınamadı: ${item.productId}`,
                productDetailsResponse.message
              );
              return item; // Orijinal item'ı döndür
            }
          });
          const results = await Promise.all(promises);
          setDetailedCartItems(results);
          
          // Kargo fiyatını hesapla
          await calculateShippingCost();
        } catch (error) {
          console.error(
            "Sepetteki ürün detayları yüklenirken genel bir hata oluştu:",
            error
          );
          toast.error("Sepetteki ürün detayları yüklenemedi.");
          // Hata durumunda, mevcut sepet item'larını fallback olarak kullan
          setDetailedCartItems(cart.items.map((item) => ({ ...item })));
        } finally {
          setCartDetailsLoading(false);
        }
      } else {
        setDetailedCartItems([]);
        setCartDetailsLoading(false);
      }
    };

    fetchCartProductDetails();
  }, [cart]); // cart context'inden gelen sepet değiştiğinde çalışır

  // Sepet değiştiğinde kargo fiyatını hemen hesapla (detailedCartItems'ı beklemeden)
  useEffect(() => {
    if (cart && (cart.items?.length > 0 || cart.customPackages?.length > 0)) {
      calculateShippingCost();
    }
  }, [cart, calculateShippingCost]);

  // Accordion işlevselliği için useEffect hook'u
  useEffect(() => {
    // Bu useEffect fonksiyonu artık gerekli değil, accordion durumları state ile yönetiliyor
  }, []);

  const handlePaymentChange = (paymentMethod) => {
    setSelectedPayment(paymentMethod);
  };

  const handleSaveAddress = (addressData) => {
    // Adres kaydedildikten sonra adresleri tekrar yükle
    fetchAddresses();
    setShowAddressModal(false);
  };



  // Adresleri tekrar yükleyen fonksiyon
  const fetchAddresses = async () => {
    try {
      // Kullanıcı giriş yapmamışsa API'ye istek gönderme
      if (!isAuthenticated()) return;
      // setLoading(true); // Eğer bu fonksiyon için ayrı bir loading state yönetilecekse
      const response = await customerService.getCustomerAddresses();
      console.log(
        "API response from getCustomerAddresses in fetchAddresses:",
        JSON.stringify(response)
      ); // API'den gelen ham yanıtı logla

      if (response.succeeded) {
        // Teslimat adresleri
        const shippingAddresses =
          response.addresses?.filter((a) => a.addressType === 1) || [];
        console.log(
          "Fetched shippingAddresses before setAddresses in fetchAddresses:",
          JSON.stringify(shippingAddresses)
        ); // State'e setlemeden önce gelen adresleri logla
        setAddresses(shippingAddresses);

        // Fatura adresleri
        const billingAddrs =
          response.addresses?.filter((a) => a.addressType === 2) || [];
        setBillingAddresses(billingAddrs);

        // Varsayılan adresi seç, yoksa ilk adresi
        const defaultAddress = shippingAddresses.find((a) => a.isDefault);
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
          setShippingAddress({
            firstName: defaultAddress.firstName || "",
            lastName: defaultAddress.lastName || "",
            company: "",
            country: "Türkiye",
            address: defaultAddress.address || "",
            apartment: "",
            city: defaultAddress.province || "",
            district: defaultAddress.district || "",
            postCode: defaultAddress.postalCode || "",
            phone: defaultAddress.phone || "",
            email: user?.email || "",
          });
        } else if (shippingAddresses.length > 0) {
          setSelectedAddressId(shippingAddresses[0].id);
          setShippingAddress({
            firstName: shippingAddresses[0].firstName || "",
            lastName: shippingAddresses[0].lastName || "",
            company: "",
            country: "Türkiye",
            address: shippingAddresses[0].address || "",
            apartment: "",
            city: shippingAddresses[0].province || "",
            district: shippingAddresses[0].district || "",
            postCode: shippingAddresses[0].postalCode || "",
            phone: shippingAddresses[0].phone || "",
            email: user?.email || "",
          });
        }

        // Varsayılan fatura adresi seç, yoksa ilk adresi
        const defaultBillingAddress = billingAddrs.find((a) => a.isDefault);
        if (defaultBillingAddress) {
          setSelectedBillingAddressId(defaultBillingAddress.id);
        } else if (billingAddrs.length > 0) {
          setSelectedBillingAddressId(billingAddrs[0].id);
        }

        setHasAddress(shippingAddresses.length > 0);
      } else {
        console.error("Adresler alınamadı:", response.message);
      }
    } catch (error) {
      console.error(
        "Adresler yüklenirken hata (fetchAddresses catch bloğu):",
        error
      );
      toast.error("Adresleriniz yüklenemedi. Lütfen tekrar deneyin.");
    } finally {
      // setLoading(false); // Eğer bu fonksiyon için ayrı bir loading state yönetilecekse
    }
  };

  // Telefon numarasını formatlama fonksiyonu
  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/^(?:\+?90|0)?(\d{10})$/, "$1");
    if (cleaned.length === 10) {
      return `+90 ${cleaned.substring(0, 3)} ${cleaned.substring(
        3,
        6
      )} ${cleaned.substring(6, 8)} ${cleaned.substring(8, 10)}`;
    }
    return phone;
  };

  // Adres değişikliğini işle
  const handleAddressChange = (e) => {
    const addressId = e.target.value;
    setSelectedAddressId(addressId);

    // Seçilen adresi bul ve shipping adres olarak ata
    const selectedAddr = addresses.find((a) => a.id === parseInt(addressId));
    if (selectedAddr) {
      setShippingAddress({
        firstName: selectedAddr.firstName || "",
        lastName: selectedAddr.lastName || "",
        company: "",
        country: "Türkiye",
        address: selectedAddr.address || "",
        apartment: "",
        city: selectedAddr.province || "",
        district: selectedAddr.district || "",
        postCode: selectedAddr.postalCode || "",
        phone: selectedAddr.phone || "",
        email: user?.email || "",
      });
    }
  };

  // Yeni adres ekleme modalını aç
  const handleAddNewAddress = (type = "shipping") => {
    setAddressModalType(type);
    setSelectedAddressForEdit(null);
    setShowAddressModal(true);
  };

  // Adres düzenleme modalını aç
  const handleEditAddress = (addressType = "shipping", addressId = null) => {
    let selectedId = addressId;

    if (!selectedId) {
      if (addressType === "shipping") {
        selectedId = selectedAddressId;
      } else {
        selectedId = selectedBillingAddressId;
      }
    }

    if (!selectedId) {
      toast.warn(
        `Lütfen düzenlemek için bir ${
          addressType === "shipping" ? "teslimat" : "fatura"
        } adresi seçin.`
      );
      return;
    }

    const addressList =
      addressType === "shipping" ? addresses : billingAddresses;
    const addressToEdit = addressList.find((a) => a.id === selectedId);

    if (addressToEdit) {
      setAddressModalType(addressType);
      setSelectedAddressForEdit(addressToEdit);
      setShowAddressModal(true);
    }
  };

  // "Faturamı teslimat adresime gönder" checkbox değişikliği
  const handleBillingAddressCheckChange = (e) => {
    setUseSameAddressForBilling(e.target.checked);
  };

  // Fatura adresi değişikliğini işle
  const handleBillingAddressChange = (e) => {
    setSelectedBillingAddressId(parseInt(e.target.value));
  };

  const handleCardInfoChange = (e) => {
    const { name, value } = e.target;

    if (name === "cardNumber") {
      // Sadece rakamları al
      const numericValue = value.replace(/\D/g, "");

      // 16 karakterle sınırla
      const truncatedValue = numericValue.slice(0, 16);

      // 4'er gruplar halinde formatla
      const formattedValue = truncatedValue.replace(/(\d{4})(?=\d)/g, "$1 ");

      setCardInfo((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));

      // Kart numarası 6 haneli BIN kodu tamamlandığında taksit seçeneklerini getiriyoruz
      // Ancak bu işlemi useEffect hook'u ile yapacağız, buradan kaldırıyoruz
    } else if (name === "cvv") {
      // Sadece sayılar için filtreleme
      const numericValue = value.replace(/\D/g, "");

      // CVV 4 karakterle sınırlı
      const truncatedValue = numericValue.slice(0, 4);

      setCardInfo((prev) => ({
        ...prev,
        [name]: truncatedValue,
      }));
    } else {
      setCardInfo((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Taksit seçeneği değişikliği - geçici olarak devre dışı
  // const handleInstallmentChange = (installment) => {
  //   setSelectedInstallment(installment);
  // };

  // Taksit seçeneklerini getiren fonksiyon - geçici olarak devre dışı
  // const fetchInstallmentOptions = useCallback(
  //   async (cardNumber) => {
  //     if (!cardNumber || cardNumber.length < 6) return;

  //     setFetchingInstallments(true);
  //     try {
  //       const binNumber = cardNumber.substring(0, 6);
  //       const serviceResult = await paymentService.getInstallmentOptions({
  //         binNumber: binNumber,
  //         price: getCartTotalPrice(),
  //       });

  //       if (
  //         serviceResult?.succeeded &&
  //         serviceResult.installmentPrices &&
  //         serviceResult.installmentPrices.length > 0
  //       ) {
  //         setInstallmentOptions(
  //           serviceResult.installmentPrices.map((option) => ({
  //             installment: option.installmentNumber,
  //             amount: option.installmentPrice,
  //           }))
  //         );
  //         setSelectedInstallment(1); // Varsayılan olarak ilk taksiti (genellikle tek çekim) seç

  //         // BIN detaylarını sakla
  //         if (serviceResult.binDetails) {
  //           setBinDetails(serviceResult.binDetails);
  //         }
  //       } else {
  //         // Başarısızlık, hata veya boş taksit listesi durumunda tek çekim göster
  //         const currentTotalPrice =
  //           typeof getCartTotalPrice() === "number" ? getCartTotalPrice() : 0;
  //         setInstallmentOptions([
  //           { installment: 1, amount: currentTotalPrice },
  //         ]);
  //         setSelectedInstallment(1);

  //         if (serviceResult && !serviceResult.succeeded) {
  //           // Servis başarısız olduysa (örn: ağ hatası, API format hatası)
  //           toast.warning(
  //             serviceResult.message ||
  //               "Taksit bilgileri alınırken bir hata oluştu."
  //           );
  //         } else if (
  //           serviceResult?.succeeded &&
  //           (!serviceResult.installmentPrices ||
  //             serviceResult.installmentPrices.length === 0)
  //         ) {
  //           // Servis başarılı ama taksit yok (örn: kart desteklemiyor, API mesajı olabilir)
  //           toast.info(
  //             serviceResult.message ||
  //               "Geçerli taksit seçeneği bulunamadı, tek çekim gösteriliyor."
  //           );
  //         }
  //         console.warn(
  //           "Taksitler alınamadı veya boş. Service Result:",
  //           serviceResult
  //         );
  //       }
  //     } catch (error) {
  //       // paymentService kendi hatalarını yakalayıp {succeeded: false} döndürmeli.
  //       // Bu blok, paymentService'de beklenmedik bir exception olursa diye burada.
  //       console.error(
  //         "fetchInstallmentOptions içinde beklenmedik hata:",
  //         error
  //       );
  //       const currentTotalPrice =
  //         typeof getCartTotalPrice() === "number" ? getCartTotalPrice() : 0;
  //       setInstallmentOptions([{ installment: 1, amount: currentTotalPrice }]);
  //       setSelectedInstallment(1);
  //       toast.error("Taksit seçenekleri alınırken kritik bir hata oluştu.");
  //     } finally {
  //       setFetchingInstallments(false);
  //     }
  //   },
  //   [getCartTotalPrice] // getCartTotalPrice CartContext'ten geldiği için useCallback dependency listesinde olmalı
  // );

  // Sözleşme modalını açan fonksiyon
  const openTermsModal = (termType) => {
    let title = "";
    let content = "";

    if (termType === "mesafeliSatis") {
      title = "Mesafeli Satış Sözleşmesi";
      // MesafeliSatisSozlesmesi değişkenini contracts.js dosyasından import etmeyi unutmayın
      import("../constants/contracts").then((contracts) => {
        setTermsModalContent({
          title,
          content: contracts.MesafeliSatisSozlesmesi,
        });
        setShowTermsModal(true);
      });
    } else if (termType === "kvkk") {
      title = "Kişisel Verilerin Korunması Hakkında Açıklama";
      // KVKKAciklamasi değişkenini contracts.js dosyasından import etmeyi unutmayın
      import("../constants/contracts").then((contracts) => {
        setTermsModalContent({
          title,
          content: contracts.KVKKAciklamasi,
        });
        setShowTermsModal(true);
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("handleSubmit triggered");
    console.log(
      "isGuestCheckout:",
      isGuestCheckout,
      "isAuthenticated:",
      isAuthenticated()
    );
    console.log("Initial guestInfo:", JSON.stringify(guestInfo));
    console.log("Initial selectedAddressId:", selectedAddressId);
    console.log("Initial addresses:", JSON.stringify(addresses));

    // Kredi kartı seçiliyse kart bilgilerini kontrol et
    if (selectedPayment === PaymentMethod.CREDIT_CARD) {
      if (
        !cardInfo.cardNumber ||
        !cardInfo.cardHolderName ||
        !cardInfo.expiryMonth ||
        !cardInfo.expiryYear ||
        !cardInfo.cvv
      ) {
        toast.error("Lütfen tüm kart bilgilerini eksiksiz giriniz.");
        return;
      }

      // Kart numarası doğrulaması (sadece rakam, 16 hane)
      const cleanCardNumber = cardInfo.cardNumber.replace(/\s/g, "");
      if (cleanCardNumber.length !== 16 || !/^\d+$/.test(cleanCardNumber)) {
        toast.error("Geçersiz kart numarası. 16 haneli olmalıdır.");
        return;
      }

      // CVV doğrulaması (3-4 hane)
      if (
        cardInfo.cvv.length < 3 ||
        cardInfo.cvv.length > 4 ||
        !/^\d+$/.test(cardInfo.cvv)
      ) {
        toast.error("Geçersiz CVV kodu. 3-4 haneli olmalıdır.");
        return;
      }

      // Son kullanma tarihi doğrulaması
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const cardYear = parseInt(cardInfo.expiryYear);
      const cardMonth = parseInt(cardInfo.expiryMonth);

      if (
        cardYear < currentYear ||
        (cardYear === currentYear && cardMonth < currentMonth)
      ) {
        toast.error("Kartın son kullanma tarihi geçmiş.");
        return;
      }

      // Kart sahibi adı doğrulaması
      if (cardInfo.cardHolderName.trim().length < 3) {
        toast.error("Kart sahibi adı en az 3 karakter olmalıdır.");
        return;
      }
    }

    // Adres ve kullanıcı tipi validasyonları
    let finalShippingAddress = {};
    let finalBillingAddressInfo = {};
    let finalUseSameAddressForBilling = useSameAddressForBilling;

    if (isGuestCheckout && !isAuthenticated()) {
      if (
        !guestInfo.firstName ||
        !guestInfo.lastName ||
        !guestInfo.email ||
        !guestInfo.phone ||
        !guestInfo.identityNumber || // Validasyona eklendi
        !guestInfo.address ||
        !guestInfo.city ||
        !guestInfo.district
      ) {
        toast.error(
          "Lütfen tüm teslimat bilgilerini (TC Kimlik No dahil) eksiksiz doldurun."
        );
        return;
      }
      if (
        guestInfo.identityNumber.length !== 11 ||
        !/^\d+$/.test(guestInfo.identityNumber)
      ) {
        toast.error(
          "Lütfen geçerli bir TC Kimlik Numarası girin (11 hane, sadece rakam)."
        );
        return;
      }
      if (!/^\S+@\S+\.\S+$/.test(guestInfo.email)) {
        toast.error("Lütfen geçerli bir e-posta adresi girin.");
        return;
      }
      finalShippingAddress = {
        firstName: guestInfo.firstName,
        lastName: guestInfo.lastName,
        company: "",
        country: "Türkiye",
        address: guestInfo.address,
        apartment: "",
        city: guestInfo.city,
        district: guestInfo.district,
        postalCode: guestInfo.postCode,
        phone: guestInfo.phone,
        email: guestInfo.email,
      };
      if (!finalShippingAddress.firstName || !finalShippingAddress.lastName) {
        toast.error("Lütfen teslimat için adınızı ve soyadınızı girin.");
        console.error(
          "Misafir için finalShippingAddress isim/soyisim eksik:",
          JSON.stringify(finalShippingAddress)
        );
        setLoading(false);
        return;
      }
      if (guestWantsDifferentBilling) {
        if (
          !guestInfo.billingFirstName ||
          !guestInfo.billingLastName ||
          !guestInfo.billingAddress ||
          !guestInfo.billingCity ||
          !guestInfo.billingDistrict
        ) {
          toast.error("Lütfen tüm fatura bilgilerini eksiksiz doldurun.");
          return;
        }
        finalBillingAddressInfo = {
          firstName: guestInfo.billingFirstName,
          lastName: guestInfo.billingLastName,
          address: guestInfo.billingAddress,
          city: guestInfo.billingCity,
          district: guestInfo.billingDistrict,
          postalCode: guestInfo.billingPostCode,
          phone: guestInfo.billingPhone || guestInfo.phone,
          email: guestInfo.billingEmail || guestInfo.email,
        };
        finalUseSameAddressForBilling = false;
      } else {
        finalBillingAddressInfo = finalShippingAddress;
        finalUseSameAddressForBilling = true;
      }
    } else if (isAuthenticated()) {
      if (!selectedAddressId) {
        toast.error("Lütfen bir teslimat adresi seçin.");
        return;
      }
      const currentShippingAddress = addresses.find(
        (a) => a.id === selectedAddressId
      );
      if (!currentShippingAddress) {
        toast.error("Seçilen teslimat adresi bulunamadı.");
        setLoading(false);
        return;
      }

      // fullName'i firstName ve lastName olarak ayır
      let ad = ""; // Ad
      let soyad = ""; // Soyad
      if (currentShippingAddress.fullName) {
        const parts = currentShippingAddress.fullName.trim().split(" ");
        if (parts.length > 1) {
          soyad = parts.pop(); // Son kelime soyad
          ad = parts.join(" "); // Kalanlar ad
        } else if (parts.length === 1) {
          ad = parts[0]; // Tek kelime ise sadece ad olarak al, soyad boş kalabilir veya varsayılan bir değer atanabilir.
        }
      }

      finalShippingAddress = {
        firstName: ad, // Ayrılmış adı kullan
        lastName: soyad, // Ayrılmış soyadı kullan
        company: currentShippingAddress.companyName || "",
        country: "Türkiye",
        address: currentShippingAddress.address || "",
        apartment: currentShippingAddress.apartment || "",
        city: currentShippingAddress.province || "",
        district: currentShippingAddress.district || "",
        postalCode: currentShippingAddress.postalCode || "",
        phone: currentShippingAddress.phone || "",
        email: user.email,
        identityNumber:
          currentShippingAddress.identityNumber || user.identityNumber, // Adresteki TC No veya kullanıcı profili
      };
      console.log(
        "currentShippingAddress in handleSubmit:",
        JSON.stringify(currentShippingAddress)
      );
      console.log(
        "finalShippingAddress in handleSubmit:",
        JSON.stringify(finalShippingAddress)
      );
      if (!finalShippingAddress.firstName || !finalShippingAddress.lastName) {
        toast.error(
          "Seçili teslimat adresinde ad veya soyad bilgisi eksik. Lütfen adresinizi güncelleyin veya farklı bir adres seçin."
        );
        console.error(
          "Kayıtlı kullanıcı için finalShippingAddress isim/soyisim eksik:",
          JSON.stringify(finalShippingAddress)
        );
        setLoading(false);
        return;
      }
      if (!useSameAddressForBilling) {
        if (!selectedBillingAddressId) {
          toast.error("Lütfen bir fatura adresi seçin.");
          return;
        }
        const currentBillingAddress = billingAddresses.find(
          (a) => a.id === selectedBillingAddressId
        );
        if (!currentBillingAddress) {
          toast.error("Seçilen fatura adresi bulunamadı.");
          return;
        }
        finalBillingAddressInfo = {
          firstName: currentBillingAddress.firstName,
          lastName: currentBillingAddress.lastName,
          address: currentBillingAddress.address,
          identityNumber:
            currentBillingAddress.identityNumber || user.identityNumber, // Fatura Adresindeki TC No veya kullanıcı profili
          city: currentBillingAddress.province,
          district: currentBillingAddress.district,
          postalCode: currentBillingAddress.postalCode,
          phone: currentBillingAddress.phone,
          email: user.email,
        };
      }
      finalUseSameAddressForBilling = useSameAddressForBilling;
    } else {
      toast.error(
        "Ödeme işlemine devam etmek için lütfen giriş yapın veya misafir olarak devam edin."
      );
      return;
    }

    if (!agreedToTerms) {
      toast.error("Devam etmek için sözleşme ve koşulları kabul etmelisiniz.");
      return;
    }

    const paymentMethodEnum =
      PaymentMethodMapping[selectedPayment] || PaymentMethod.CREDIT_CARD;

    if (paymentMethodEnum === PaymentMethod.CREDIT_CARD) {
      if (
        !cardInfo.cardNumber ||
        cardInfo.cardNumber.replace(/\\s/g, "").length < 16 ||
        !cardInfo.cardHolderName ||
        !cardInfo.expiryMonth ||
        !cardInfo.expiryYear ||
        !cardInfo.cvv ||
        cardInfo.cvv.length < 3
      ) {
        toast.error(
          "Lütfen kredi kartı bilgilerinizi eksiksiz ve doğru doldurun."
        );
        return;
      }
    }

    setLoading(true);
    try {
      let buyerId = "";
      if (isAuthenticated() && user) {
        buyerId = user.id || user.userId || user.sub || uuidv4();
      } else if (isGuestCheckout) {
        buyerId = guestInfo.email || uuidv4();
      } else {
        buyerId = uuidv4();
      }

      const totalPriceForIyzico = getCartTotalPrice();

      const finalBasketItems = [];
      if (cart && cart.items && detailedCartItems.length > 0) {
        cart.items.forEach((cartItem) => {
          const productDetail = detailedCartItems.find(
            (detail) => detail.productId === cartItem.productId
          );
          if (productDetail) {
            for (let i = 0; i < cartItem.quantity; i++) {
              finalBasketItems.push({
                id: productDetail.productId,
                price: (productDetail.discountedPrice &&
                productDetail.discountedPrice > 0
                  ? productDetail.discountedPrice
                  : productDetail.price
                ).toFixed(2),
                name: productDetail.productName,
                category1: productDetail.category1 || "Pestil & Köme",
                category2: productDetail.category2 || "Çeşitleri",
                itemType: "PHYSICAL",
              });
            }
          } else {
            console.warn(
              `Product detail not found for cart item ID: ${cartItem.productId} during basketItems creation.`
            );
            for (let i = 0; i < cartItem.quantity; i++) {
              finalBasketItems.push({
                id: cartItem.productId,
                price: cartItem.price ? cartItem.price.toFixed(2) : "0.00",
                name: cartItem.productName || "Bilinmeyen Ürün",
                category1: "Hata Kategorisi",
                category2: "Hata Alt Kategori",
                itemType: "PHYSICAL",
              });
            }
          }
        });
      }
      // Sepetteki özel paketleri basketItems dizisine ekliyorum
      if (cart && cart.customPackages && cart.customPackages.length > 0) {
        cart.customPackages.forEach((pkg) => {
          const pkgTotalPrice = pkg.items.reduce(
            (sum, item) => sum + (item.totalPrice || (item.unitPrice * item.quantity)),
            0
          );
          finalBasketItems.push({
            id: pkg.id,
            price: pkgTotalPrice.toFixed(2),
            name: "Özelleştirilmiş Paket",
            category1: "Pestil & Köme",
            category2: "Özelleştirilmiş Paket",
            itemType: "PHYSICAL",
          });
        });
      }

      const formatDateForIyzico = (dateStringOrDate) => {
        const date = new Date(dateStringOrDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      if (paymentMethodEnum === PaymentMethod.CREDIT_CARD) {
        // 1. Create Order Payload for Credit Card
        const orderItemsForCreditCard = [
          // Product items
          ...detailedCartItems.map((item) => {
            // Kilogram bazlı ürünlerde quantity her zaman 1 olmalı
            const saleType = item.saleType !== undefined ? item.saleType : (item.weightInGrams || item.weightGrams ? 1 : 0);
            const quantity = saleType === 1 ? 1 : item.quantity;
            
            return {
              productId: item.productId,
              quantity: quantity,
              price: item.price,
              discountedPrice: item.discountedPrice,
              weightInGrams: item.weightInGrams || item.weightGrams,
            };
          }),
          // Custom package summaries
          ...(cart?.customPackages?.map((pkg) => {
            const pkgTotal = pkg.items.reduce(
              (sum, itm) => sum + (itm.totalPrice || (itm.unitPrice * itm.quantity)),
              0
            );
            const description = pkg.items
              .map((itm) => {
                const weightLabel =
                  itm.weightGrams >= 1000
                    ? `${(itm.weightGrams / 1000).toFixed(2)}kg`
                    : `${itm.weightGrams}g`;
                return `${itm.quantity}× ${itm.productName} (${weightLabel})`;
              })
              .join(", ");
            return {
              productId: "00000000-0000-0000-0000-000000000000",
              quantity: 1,
              price: pkgTotal,
              discountedPrice: null,
              displayName: `Özelleştirilmiş Paket: ${description}`,
            };
          }) || []),
        ];
        const orderPayloadForCreditCard = {
          paymentMethod: paymentMethodEnum,
          items: orderItemsForCreditCard,
          shippingAddress: finalShippingAddress,
          useSameAddressForBilling: finalUseSameAddressForBilling,
          billingAddress: !finalUseSameAddressForBilling
            ? finalBillingAddressInfo
            : null,
          notes: orderNotes,
          isGuestOrder: isGuestCheckout && !isAuthenticated(),
          totalAmount: totalPriceForIyzico,
          finalAmount: finalDisplayPrice,
        };

        let orderResult;
        try {
          orderResult = await orderService.createOrder(
            orderPayloadForCreditCard
          );
          if (!orderResult || !orderResult.succeeded || !orderResult.orderId) {
            toast.error(
              orderResult?.message || "Sipariş oluşturulurken bir hata oluştu."
            );
            setLoading(false);
            return;
          }
        } catch (orderError) {
          console.error(
            "Kredi kartı için sipariş oluşturma hatası:",
            orderError
          );
          toast.error(
            "Siparişiniz oluşturulurken bir sorunla karşılaşıldı. Lütfen tekrar deneyin."
          );
          setLoading(false);
          return;
        }

        // 2. Initialize PayTR Payment with the created Order ID
        // Client IP'sini önceden al
        const clientIp = await getClientIp();

        // BIN detaylarından cardType'ı al, yoksa varsayılan olarak "credit" kullan
        const cardType = binDetails?.cardType || "credit";

        console.log("BIN Details for payment:", binDetails);
        console.log("Selected card type for PayTR:", cardType);

        const payTRPaymentRequest = {
          orderId: orderResult.orderId,
          paymentAmount: finalDisplayPrice,
          userName: `${finalShippingAddress.firstName} ${finalShippingAddress.lastName}`,
          userAddress: finalShippingAddress.address,
          email: finalShippingAddress.email,
          userPhone: finalShippingAddress.phone.replace(/[^0-9]/g, ""),
          userIp: clientIp, // Client IP'sini ekle
          currency: "TL",
          noInstallment: 1, // Taksit geçici olarak devre dışı - sadece tek çekim
          maxInstallment: 0, // Taksit geçici olarak devre dışı
          lang: "tr",
          basketItems: finalBasketItems.map((item) => ({
            name: item.name,
            price: item.price,
            quantity: item.quantity,
          })),
          testMode: process.env.NODE_ENV === "development",
          cardNumber: cardInfo.cardNumber.replace(/\s/g, ""),
          cardHolderName: cardInfo.cardHolderName,
          expiryMonth: parseInt(cardInfo.expiryMonth),
          expiryYear: parseInt(cardInfo.expiryYear),
          cvv: cardInfo.cvv,
          cardType: cardType,
        };

        console.log(
          "payTRPaymentRequest for 3DS:",
          JSON.stringify(payTRPaymentRequest, null, 2)
        );

        const paymentResponse = await paymentService.initializePayTRPayment(
          payTRPaymentRequest
        );

        if (
          paymentResponse &&
          paymentResponse.success &&
          (paymentResponse.htmlContent || paymentResponse.is3DSecureRequired)
        ) {
          // Store orderId to use in callback
          localStorage.setItem("payTROrderId", orderResult.orderId);
          localStorage.setItem("payTRMerchantOid", paymentResponse.merchantOid);

          // Sepet bilgilerini kaydet (sayfa yenilendiğinde kullanmak için)
          localStorage.setItem(
            "checkoutState",
            JSON.stringify({
              orderId: orderResult.orderId,
              totalAmount: finalDisplayPrice,
              timestamp: new Date().toISOString(),
            })
          );

          if (
            paymentResponse.is3DSecureRequired &&
            paymentResponse.htmlContent
          ) {
            // 3D Secure HTML content'ini doğrudan sayfada göster
            console.log("PayTR 3DS HTML content sayfada gösteriliyor");
            setThreeDSecureHtmlContent(paymentResponse.htmlContent);
            setShow3DSecurePage(true);
            setLoading(false);
            return;
          } else if (paymentResponse.iframeUrl) {
            // Eski metod: direct redirect
            console.log(
              "PayTR 3DS sayfasına yönlendiriliyor:",
              paymentResponse.iframeUrl
            );
            window.location.href = paymentResponse.iframeUrl;
            return;
          }

          // Örnek: await orderService.updateOrderStatus(cart?.id, "PaymentFailed", paymentResponse?.errorCode, paymentResponse?.errorMessage);
          // It would be better to use the actual orderId if available
          if (orderResult && orderResult.orderId) {
            // Try to update order status to PaymentFailed if Iyzico init failed
            try {
              await orderService.updateOrderStatus(
                orderResult.orderId,
                "PaymentFailed",
                paymentResponse?.errorCode || "PAYTR_INIT_FAILED",
                paymentResponse?.errorMessage || "PayTR ödeme başlatılamadı"
              );
            } catch (statusUpdateError) {
              console.error(
                "Sipariş durumu güncelleme hatası (ödeme başlatılamadı):",
                statusUpdateError
              );
            }
          }
          setLoading(false);
        } else {
          console.error("PayTR 3DS başlatma hatası:", paymentResponse);
          toast.error(
            paymentResponse?.message ||
              paymentResponse?.errorMessage ||
              "Ödeme işlemi başlatılamadı. Lütfen bilgilerinizi kontrol edin veya farklı bir ödeme yöntemi deneyin."
          );
          // Sipariş oluşturma veya güncelleme (başarısız ödeme durumu için)
          // Örnek: await orderService.updateOrderStatus(cart?.id, "PaymentFailed", paymentResponse?.errorCode, paymentResponse?.errorMessage);
          setLoading(false);
        }
      } else if (paymentMethodEnum === PaymentMethod.BANK_TRANSFER) {
        // Banka Havalesi için sipariş oluşturma ve yönlendirme
        // Bu kısım için öncelikle bir backend order endpoint'ine ihtiyaç var.
        // Frontend'den direkt orderId ile yönlendirme yapmak yerine,
        // backend'de order oluşturup, o orderId ile yönlendirme yapmak daha güvenli.

        // Örnek bir order payload (Iyzico öncesi createOrder gibi)
        const orderItemsForBankTransfer = [
          // Product items
          ...detailedCartItems.map((item) => {
            // Kilogram bazlı ürünlerde quantity her zaman 1 olmalı
            const saleType = item.saleType !== undefined ? item.saleType : (item.weightInGrams || item.weightGrams ? 1 : 0);
            const quantity = saleType === 1 ? 1 : item.quantity;
            
            return {
              productId: item.productId,
              quantity: quantity,
              price: item.price,
              discountedPrice: item.discountedPrice,
              weightInGrams: item.weightInGrams || item.weightGrams,
            };
          }),
          // Custom package summaries
          ...(cart?.customPackages?.map((pkg) => {
            const pkgTotal = pkg.items.reduce(
              (sum, itm) => sum + (itm.totalPrice || (itm.unitPrice * itm.quantity)),
              0
            );
            const description = pkg.items
              .map((itm) => `${itm.quantity}× ${itm.productName}`)
              .join(", ");
            return {
              productId: "00000000-0000-0000-0000-000000000000",
              quantity: 1,
              price: pkgTotal,
              discountedPrice: null,
              displayName: `Özelleştirilmiş Paket: ${description}`,
            };
          }) || []),
        ];
        const orderPayloadForBankTransfer = {
          paymentMethod: paymentMethodEnum,
          items: orderItemsForBankTransfer,
          shippingAddress: finalShippingAddress,
          useSameAddressForBilling: finalUseSameAddressForBilling,
          billingAddress: !finalUseSameAddressForBilling
            ? finalBillingAddressInfo
            : null,
          notes: orderNotes,
          isGuestOrder: isGuestCheckout && !isAuthenticated(),
          // Bu sipariş için toplam tutarlar da eklenebilir
          totalAmount: totalPriceForIyzico, // Sepet ürünlerinin toplamı
          finalAmount: finalDisplayPrice, // Müşterinin ödeyeceği (kargo vs dahil olabilir)
        };

        try {
          const orderResult = await orderService.createOrder(
            orderPayloadForBankTransfer
          );
          if (orderResult && orderResult.succeeded && orderResult.orderId) {
            setLoading(false);
            navigate(`/bank-transfer?orderId=${orderResult.orderId}`);
          } else {
            toast.error(
              orderResult?.message || "Sipariş oluşturulurken bir hata oluştu."
            );
            setLoading(false);
          }
        } catch (bankError) {
          console.error(
            "Banka havalesi için sipariş oluşturma hatası:",
            bankError
          );
          toast.error("Siparişiniz oluşturulurken bir sorunla karşılaşıldı.");
          setLoading(false);
        }
      } else {
        // Diğer ödeme yöntemleri (varsa)
        toast.warn("Seçilen ödeme yöntemi şu anda desteklenmiyor.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Ödeme işlemi sırasında genel hata:", error);
      toast.error(
        error.message ||
          "Ödeme sırasında bir hata oluştu. Lütfen tekrar deneyin."
      );
      setLoading(false);
    }
  };

  // Önceki BIN numarasını saklayan bir ref tutuyoruz
  const [lastBinNumber, setLastBinNumber] = useState("");

  // Kart numarası değişirse ve BIN kodu 6 haneye ulaştıysa taksit seçeneklerini getir - geçici olarak devre dışı
  // useEffect(() => {
  //   const numericValue = cardInfo.cardNumber.replace(/\D/g, "");
  //   const binNumber = numericValue.substring(0, 6);

  //   // BIN numarası 6 haneye ulaştığında ve son sorgulanan BIN numarasından farklı olduğunda sorgula
  //   if (binNumber.length === 6 && binNumber !== lastBinNumber) {
  //     fetchInstallmentOptions(numericValue);
  //     setLastBinNumber(binNumber);
  //   } else if (numericValue.length < 6) {
  //     // Kart numarası 6 haneden azsa ve taksit seçenekleri yoksa, tek çekim ekle
  //     if (installmentOptions.length === 0) {
  //       setInstallmentOptions([
  //         { installment: 1, amount: getCartTotalPrice() },
  //       ]);
  //       setSelectedInstallment(1);
  //     }
  //     // BIN detaylarını temizle
  //     setBinDetails(null);
  //   }
  // }, [cardInfo.cardNumber, fetchInstallmentOptions, lastBinNumber]);

  // Sepet tutarı değiştiğinde ve seçili bir kart varsa taksit seçeneklerini güncelle
  // Bu hook'u tamamen kaldırıyoruz, BIN değişikliği kontrolü yeterli olacak

  // Misafir kullanıcı bilgi girişlerini işleyen fonksiyon
  const handleGuestInfoChange = (e) => {
    const { name, value } = e.target;
    setGuestInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Misafir kullanıcı için "farklı fatura adresi" checkbox değişikliği
  const handleGuestBillingCheckChange = (e) => {
    setGuestWantsDifferentBilling(e.target.checked);
  };

  // Sayfa yüklendiğinde PayTR callback kontrolü yap
  useEffect(() => {
    const checkPayTRCallback = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paytrStatus = urlParams.get("paytr_status");
      const orderId = urlParams.get("orderId");

      if (paytrStatus === "success" && orderId) {
        clearCart();
        toast.success("Ödemeniz başarıyla tamamlandı!");
        navigate(`/order-success?orderId=${orderId}`);
      } else if (paytrStatus === "fail") {
        toast.error("Ödeme işlemi başarısız oldu. Lütfen tekrar deneyin.");
        // URL'den parametreleri temizle
        navigate("/checkout", { replace: true });
      }
    };

    checkPayTRCallback();
  }, [navigate, clearCart]);

  // Yükleme durumunda
  if (loading) {
    return (
      <section className="checkout py-80">
        <div className="container container-lg">
          <LoadingSpinner 
            size="lg" 
            color="success" 
            text="Bilgileriniz yükleniyor..."
          />
        </div>
      </section>
    );
  }

  // Adres ve kullanıcı bilgilerinin yeterliliğini kontrol et
  const checkAddressDataSufficient = () => {
    if (isAuthenticated()) {
      return !!selectedAddressId; // Kayıtlı kullanıcı için adres seçimi yeterli
    }
    if (isGuestCheckout) {
      // Misafir kullanıcı için tüm zorunlu alanların dolu olması gerekir
      return (
        !!guestInfo.firstName &&
        !!guestInfo.lastName &&
        !!guestInfo.email &&
        !!guestInfo.phone &&
        !!guestInfo.identityNumber &&
        guestInfo.identityNumber.length === 11 && // TC Kimlik No 11 hane olmalı
        /^\d+$/.test(guestInfo.identityNumber) && // TC Kimlik No sadece rakam içermeli
        /^\S+@\S+\.\S+$/.test(guestInfo.email) && // Geçerli e-posta formatı
        !!guestInfo.address &&
        !!guestInfo.city &&
        !!guestInfo.district
      );
    }
    return false; // Diğer durumlarda (örn: ne kayıtlı ne de misafir)
  };

  const isAddressDataSufficient = checkAddressDataSufficient();
  const isButtonDisabled =
    loading || !agreedToTerms || !isAddressDataSufficient;

  // 3DS sayfası gösteriliyorsa sadece HTML content'i render et
  if (show3DSecurePage && threeDSecureHtmlContent) {
    return (
      <div className="paytr-3ds-fullpage">
        <iframe
          srcDoc={threeDSecureHtmlContent}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 10000
          }}
          title="3D Secure Doğrulama"
        />
      </div>
    );
  }

  return (
    <section className="checkout py-80">
      <div className="container container-lg">
        <div className="row">
          {/* Sol sütun - Adres ve Sözleşmeler */}
          <div className="col-xl-9 col-lg-8">
            {/* Sepetindeki Ürünler Bölümü */}
            <div className="common-accordion mb-4">
              <div className="accordion-item rounded-8 shadow-sm border">
                <div className="accordion-header">
                  <div
                    className={`accordion-button ${
                      !accordionStates.cartItems ? "collapsed" : ""
                    }`}
                    onClick={() => toggleAccordion("cartItems")}
                    onMouseEnter={() => handleMouseEnter("cartItems")}
                    onMouseLeave={() => handleMouseLeave("cartItems")}
                    aria-expanded={accordionStates.cartItems}
                    style={getHeaderStyle("cartItems")}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle bg-success d-flex align-items-center justify-content-center me-2"
                          style={{
                            width: "24px",
                            height: "24px",
                            color: "white",
                            fontSize: "14px",
                            minWidth: "24px",
                          }}
                        >
                          1
                        </div>
                        <span className="ms-2">
                          Sepetindeki Ürünler ({cart?.items?.length || 0})
                        </span>
                      </div>
                      <div className="accordion-icon">
                        {accordionStates.cartItems ? (
                          <FaChevronUp
                            className={
                              hoverStates.cartItems
                                ? "text-success"
                                : "text-muted"
                            }
                          />
                        ) : (
                          <FaChevronDown
                            className={
                              hoverStates.cartItems
                                ? "text-success"
                                : "text-muted"
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* İçerik */}
                <div
                  className={`accordion-collapse collapse ${
                    accordionStates.cartItems ? "show" : ""
                  }`}
                >
                  <div className="accordion-body">
                    {cartDetailsLoading ? (
                      <div className="text-center py-3">
                        <LoadingSpinner 
                          size="sm" 
                          color="primary" 
                          text="Sepet ürünleri yükleniyor..."
                        />
                      </div>
                    ) : detailedCartItems.length > 0 ? (
                      detailedCartItems.map((item, index) => (
                        <div
                          key={item.id} // item.id cartItem'a ait unique id olmalı
                          className={`d-flex py-2 ${
                            index !== detailedCartItems.length - 1
                              ? "border-bottom"
                              : ""
                          }`}
                        >
                          <div
                            className="flex-shrink-0"
                            style={{ width: "60px", height: "60px" }}
                          >
                            {item.productImageUrl ? (
                              <img
                                src={
                                  item.productImageUrl.startsWith("http")
                                    ? item.productImageUrl
                                    : `${imageApiUrl}${item.productImageUrl}`
                                }
                                alt={item.productName} // Artık detailedCartItems'dan geliyor
                                className="img-fluid rounded"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                className="bg-light rounded d-flex align-items-center justify-content-center"
                                style={{ width: "100%", height: "100%" }}
                              >
                                <img
                                  src="assets/images/logo/logo.png"
                                  alt="Logo"
                                  className="img-fluid p-2"
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                          <div
                            className="ms-3 flex-grow-1 product-name"
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              flexDirection: "column",
                              alignItems: "start",
                              paddingLeft: "15px",
                            }}
                          >
                            <h6 className="mb-1">{item.productName}</h6>{" "}
                            {/* Artık detailedCartItems'dan geliyor */}
                            <p className="text-muted mb-0 small">
                              {(() => {
                                // SaleType kontrolü yap - undefined ise weightGrams/weightInGrams varlığına göre belirle
                                const saleType = item.saleType !== undefined 
                                  ? item.saleType 
                                  : (item.weightGrams || item.weightInGrams ? 1 : 0);
                                
                                if (saleType === 1) {
                                  // Kilogram bazlı ürün - gram cinsinden göster
                                  const weightGrams = item.weightGrams || item.weightInGrams || 0;
                                  return formatQuantityWithUnit(weightGrams);
                                } else {
                                  // Adet bazlı ürün
                                  return `Adet: ${item.quantity}`;
                                }
                              })()}
                            </p>
                          </div>
                          <div className="text-end d-flex flex-column justify-content-center">
                            <p className="fw-bold mb-0">
                              {(
                                (item.discountedPrice !== null &&
                                item.discountedPrice !== undefined
                                  ? item.discountedPrice
                                  : item.price) * // İndirimli fiyat varsa onu, yoksa normal fiyatı kullan
                                item.quantity
                              ).toFixed(2)}{" "}
                              ₺
                            </p>
                            {item.discountedPrice !== null &&
                              item.discountedPrice !== undefined &&
                              item.discountedPrice !== item.price && (
                                <small className="text-decoration-line-through text-muted">
                                  {(item.price * item.quantity).toFixed(2)} ₺
                                </small>
                              )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-3">
                        <p className="text-muted mb-0">
                          Sepetinizde ürün bulunmamaktadır.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Kullanıcı Giriş/Kayıt/Misafir Seçenekleri */}
            {!isAuthenticated() && !isGuestCheckout && (
              <div className="mb-4 p-4 border rounded-8 shadow-sm bg-light">
                <h5 className="mb-3">Nasıl Devam Etmek İstersiniz?</h5>
                <p>
                  Hızlı ve kolay bir alışveriş deneyimi için giriş yapabilir,
                  yeni bir hesap oluşturabilir veya misafir olarak devam
                  edebilirsiniz.
                </p>
                <div className="d-grid gap-2 d-md-flex">
                  <Link to="/login" className="btn btn-success flex-fill">
                    Giriş Yap
                  </Link>
                  <Link
                    to="/register"
                    className="btn btn-outline-success flex-fill"
                  >
                    Kayıt Ol
                  </Link>
                  <button
                    className="btn btn-info text-white flex-fill"
                    onClick={() => {
                      setIsGuestCheckout(true);
                      // Misafir seçildiğinde adres accordion'unu aç
                      if (!accordionStates.address) toggleAccordion("address");
                    }}
                  >
                    Misafir Olarak Devam Et
                  </button>
                </div>
              </div>
            )}

            {/* Teslimat Adresi Bölümü veya Misafir Bilgi Formu */}
            {(isAuthenticated() || isGuestCheckout) && (
              <div className="common-accordion mb-4">
                <div className="accordion-item rounded-8 shadow-sm border">
                  <div className="accordion-header">
                    <div
                      className={`accordion-button ${
                        !accordionStates.address ? "collapsed" : ""
                      }`}
                      onClick={() => toggleAccordion("address")}
                      onMouseEnter={() => handleMouseEnter("address")}
                      onMouseLeave={() => handleMouseLeave("address")}
                      aria-expanded={accordionStates.address}
                      style={getHeaderStyle("address")}
                    >
                      <div className="d-flex align-items-center justify-content-between w-100">
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle bg-success d-flex align-items-center justify-content-center me-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              color: "white",
                              fontSize: "14px",
                              minWidth: "24px",
                            }}
                          >
                            {"2"} {/* YENİ KOD: Her zaman 2 gösterir */}
                          </div>
                          <span className="ms-2">
                            {isGuestCheckout && !isAuthenticated()
                              ? "Teslimat ve İletişim Bilgileri"
                              : "Adres Bilgileri"}
                          </span>
                        </div>
                        <div className="accordion-icon">
                          {accordionStates.address ? (
                            <FaChevronUp
                              className={
                                hoverStates.address
                                  ? "text-success"
                                  : "text-muted"
                              }
                            />
                          ) : (
                            <FaChevronDown
                              className={
                                hoverStates.address
                                  ? "text-success"
                                  : "text-muted"
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`accordion-collapse collapse ${
                      accordionStates.address ? "show" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      {isAuthenticated() &&
                        hasAddress &&
                        addresses.length > 0 && (
                          // Kayıtlı kullanıcı için adres seçimi (mevcut kod)
                          <div className="container-fluid px-0">
                            <div className="row address-selection">
                              {addresses.map((address) => (
                                <div key={address.id} className="col-lg-6 mb-3">
                                  <label
                                    className={`address-card p-4 rounded-8 w-100 position-relative border ${
                                      selectedAddressId === address.id
                                        ? "border-success"
                                        : "border-light"
                                    }`}
                                    style={{
                                      cursor: "pointer",
                                      display: "block",
                                      backgroundColor: "var(--body-bg)",
                                      borderColor: "#ACACAC",
                                      transition: "all 0.3s ease",
                                    }}
                                    htmlFor={`address-${address.id}`}
                                  >
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                      <div className="d-flex align-items-center">
                                        <div className="common-check common-radio me-2 mb-0">
                                          <input
                                            type="radio"
                                            name="addressSelection"
                                            id={`address-${address.id}`}
                                            value={address.id}
                                            checked={
                                              selectedAddressId === address.id
                                            }
                                            onChange={() => {
                                              setSelectedAddressId(address.id);
                                              setShippingAddress({
                                                firstName:
                                                  address.firstName || "",
                                                lastName:
                                                  address.lastName || "",
                                                company: "",
                                                country: "Türkiye",
                                                address: address.address || "",
                                                apartment: "",
                                                city: address.province || "",
                                                district:
                                                  address.district || "",
                                                postCode:
                                                  address.postalCode || "",
                                                phone: address.phone || "",
                                                email: user?.email || "",
                                              });
                                            }}
                                            className="form-check-input mt-0"
                                          />
                                        </div>
                                        <h6 className="mb-0 text-heading fw-semibold">
                                          {address.address}
                                        </h6>
                                      </div>

                                      {/* Varsayılan Rozeti: Görünürlüğü address.isDefault'a bağlıdır */}
                                      {address.isDefault && (
                                        <span className="badge badge-success rounded-pill">
                                          {" "}
                                          {/* Standart Bootstrap sınıfı */}
                                          Varsayılan
                                        </span>
                                      )}
                                    </div>

                                    <div
                                      className="ps-4 text-body"
                                      style={{
                                        paddingLeft: "calc(18px + 0.5rem)",
                                      }}
                                    >
                                      <p className="mb-1 fs-sm">
                                        <strong>
                                          {address.firstName} {address.lastName}
                                        </strong>
                                      </p>
                                      <p className="mb-1 fs-sm">
                                        {address.address
                                          ?.split(",")
                                          .slice(1)
                                          .join(",") || ""}
                                      </p>
                                      <p className="mb-1 fs-sm">
                                        {address.district} / {address.province}
                                      </p>
                                      <p className="mb-0 fs-sm">
                                        {formatPhoneNumber(address.phone)}
                                      </p>
                                    </div>

                                    {/* Düzenle Butonu: Standart Bootstrap sınıfları kullanıldı */}
                                    <div className="text-end m-8 position-absolute bottom-0 end-0">
                                      <button
                                        type="button"
                                        className="btn btn-success btn-sm" // Standart Bootstrap sınıfları
                                        style={{ lineHeight: 1.2 }} // İkonu dikeyde ortalamak için eklendi
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleEditAddress(
                                            "shipping",
                                            address.id
                                          );
                                        }}
                                      >
                                        <FaEdit />
                                      </button>
                                    </div>
                                  </label>
                                </div>
                              ))}
                            </div>

                            {/* Faturamı teslimat adresime gönder ve Yeni Adres Ekle butonları */}
                            <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">
                              <div className="form-check ms-2 mb-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="billingCheck"
                                  checked={useSameAddressForBilling}
                                  onChange={handleBillingAddressCheckChange}
                                  style={{ width: "16px", height: "16px" }}
                                />
                                <label
                                  className="form-check-label ms-1"
                                  htmlFor="billingCheck"
                                  style={{ fontSize: "14px", color: "#555" }}
                                >
                                  Faturamı teslimat adresime gönder
                                </label>
                              </div>

                              <button
                                className="btn btn-success rounded-5 mb-2"
                                style={{
                                  fontSize: "14px",
                                  padding: "8px 16px",
                                }}
                                onClick={() => handleAddNewAddress("shipping")}
                              >
                                <FaPlus className="me-1" /> Yeni Adres Ekle
                              </button>
                            </div>

                            {/* Fatura adresi seçimi bölümü - useSameAddressForBilling false ise göster */}
                            {!useSameAddressForBilling && (
                              <div className="mt-4 border-top pt-3">
                                <h6 className="mb-3 small text-muted">
                                  Fatura Adresi Seçin
                                </h6>

                                {billingAddresses &&
                                billingAddresses.length > 0 ? (
                                  <div className="row">
                                    {billingAddresses.map((address) => (
                                      <div
                                        key={address.id}
                                        className="col-lg-6 mb-3"
                                      >
                                        <label
                                          className={`address-card p-4 rounded-8 w-100 position-relative border ${
                                            selectedBillingAddressId ===
                                            address.id
                                              ? "border-success"
                                              : "border-light"
                                          }`}
                                          style={{
                                            cursor: "pointer",
                                            display: "block",
                                            backgroundColor: "var(--body-bg)",
                                            borderColor: "#ACACAC",
                                            transition: "all 0.3s ease",
                                          }}
                                          htmlFor={`billing-address-${address.id}`}
                                          onClick={() =>
                                            setSelectedBillingAddressId(
                                              address.id
                                            )
                                          }
                                        >
                                          <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div className="d-flex align-items-center">
                                              <div className="common-check common-radio me-2 mb-0">
                                                <input
                                                  type="radio"
                                                  name="billingAddressSelection"
                                                  id={`billing-address-${address.id}`}
                                                  value={address.id}
                                                  checked={
                                                    selectedBillingAddressId ===
                                                    address.id
                                                  }
                                                  onChange={
                                                    handleBillingAddressChange
                                                  }
                                                  onClick={() =>
                                                    setSelectedBillingAddressId(
                                                      address.id
                                                    )
                                                  }
                                                  className="form-check-input mt-0"
                                                />
                                              </div>
                                              <h6 className="mb-0 text-heading fw-semibold">
                                                {address.address?.split(
                                                  ","
                                                )[0] || "Adres"}
                                              </h6>
                                            </div>

                                            {/* Varsayılan Rozeti */}
                                            {address.isDefault && (
                                              <span className="badge badge-success rounded-pill">
                                                Varsayılan
                                              </span>
                                            )}
                                          </div>

                                          <div
                                            className="ps-4 text-body"
                                            style={{
                                              paddingLeft:
                                                "calc(18px + 0.5rem)",
                                            }}
                                          >
                                            <p className="mb-1 fs-sm">
                                              <strong>
                                                {address.firstName}{" "}
                                                {address.lastName}
                                              </strong>
                                            </p>
                                            <p className="mb-1 fs-sm">
                                              {address.address
                                                ?.split(",")
                                                .slice(1)
                                                .join(",") || ""}
                                            </p>
                                            <p className="mb-1 fs-sm">
                                              {address.district} /{" "}
                                              {address.province}
                                            </p>
                                            <p className="mb-0 fs-sm">
                                              {formatPhoneNumber(address.phone)}
                                            </p>
                                          </div>

                                          {/* Düzenle Butonu */}
                                          <div className="text-end m-8 position-absolute bottom-0 end-0">
                                            <button
                                              type="button"
                                              className="btn btn-success btn-sm"
                                              style={{ lineHeight: 1.2 }}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleEditAddress(
                                                  "billing",
                                                  address.id
                                                );
                                              }}
                                            >
                                              <FaEdit />
                                            </button>
                                          </div>
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-3">
                                    <p>
                                      Kayıtlı fatura adresi bulunamadı. Lütfen
                                      adres ekleyin.
                                    </p>
                                  </div>
                                )}

                                <div className="text-end mt-2">
                                  <button
                                    className="btn btn-success rounded-5"
                                    style={{
                                      fontSize: "14px",
                                      padding: "8px 16px",
                                    }}
                                    onClick={() =>
                                      handleAddNewAddress("billing")
                                    }
                                  >
                                    <FaPlus className="me-1" /> Yeni Fatura
                                    Adresi Ekle
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      {isAuthenticated() &&
                        (!hasAddress || addresses.length === 0) && (
                          // Kayıtlı kullanıcı ama adresi yoksa (mevcut kod)
                          <div className="text-center py-3">
                            <p>
                              Teslimat adresi bulunamadı. Lütfen adres ekleyin.
                            </p>
                            <button
                              className="btn btn-success mt-2"
                              onClick={() => handleAddNewAddress("shipping")}
                            >
                              Adres Ekle
                            </button>
                          </div>
                        )}

                      {/* Misafir Kullanıcı Bilgi Formu */}
                      {isGuestCheckout && !isAuthenticated() && (
                        <form
                          onSubmit={(e) => e.preventDefault()}
                          className="guest-info-form needs-validation mt-24 px-lg-2"
                          noValidate
                        >
                          {/* Teslimat Bilgileri Başlığı (isteğe bağlı) */}
                          {/* <h6 className="mb-20 fw-medium">Teslimat ve İletişim Bilgileri</h6> */}

                          <div className="row gx-3 gy-3">
                            <div className="col-md-6">
                              <label
                                htmlFor="guestFirstName"
                                className="form-label form-label-lg"
                              >
                                Adınız <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-lg"
                                id="guestFirstName"
                                name="firstName"
                                value={guestInfo.firstName}
                                onChange={handleGuestInfoChange}
                                required
                              />
                              <div className="invalid-feedback">
                                Lütfen adınızı giriniz.
                              </div>
                            </div>
                            <div className="col-md-6">
                              <label
                                htmlFor="guestLastName"
                                className="form-label form-label-lg"
                              >
                                Soyadınız <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-lg"
                                id="guestLastName"
                                name="lastName"
                                value={guestInfo.lastName}
                                onChange={handleGuestInfoChange}
                                required
                              />
                              <div className="invalid-feedback">
                                Lütfen soyadınızı giriniz.
                              </div>
                            </div>
                            <div className="col-md-6">
                              <label
                                htmlFor="guestEmail"
                                className="form-label form-label-lg"
                              >
                                E-posta Adresiniz{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="email"
                                className="form-control form-control-lg"
                                id="guestEmail"
                                name="email"
                                value={guestInfo.email}
                                onChange={handleGuestInfoChange}
                                required
                              />
                              <div className="invalid-feedback">
                                Lütfen geçerli bir e-posta adresi giriniz.
                              </div>
                            </div>
                            <div className="col-md-6">
                              <label
                                htmlFor="guestPhone"
                                className="form-label form-label-lg"
                              >
                                Telefon Numaranız{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="tel"
                                className="form-control form-control-lg"
                                id="guestPhone"
                                name="phone"
                                value={guestInfo.phone}
                                onChange={handleGuestInfoChange}
                                placeholder="5XXXXXXXXX"
                                required
                              />
                              <div className="invalid-feedback">
                                Lütfen telefon numaranızı giriniz.
                              </div>
                            </div>
                            {/* TC Kimlik No Alanı - Misafir için */}
                            <div className="col-md-6">
                              <label
                                htmlFor="guestIdentityNumber"
                                className="form-label form-label-lg"
                              >
                                TC Kimlik Numarası{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-lg"
                                id="guestIdentityNumber"
                                name="identityNumber"
                                value={guestInfo.identityNumber}
                                onChange={handleGuestInfoChange}
                                placeholder="11 haneli rakamlar"
                                maxLength="11"
                                pattern="\d{11}"
                                title="TC Kimlik Numarası 11 haneli ve sadece rakamlardan oluşmalıdır."
                                required
                              />
                              <div className="invalid-feedback">
                                Lütfen TC Kimlik Numaranızı giriniz.
                              </div>
                              <small className="form-text text-muted">
                                Faturanız ve güvenli ödeme işleminiz için
                                gereklidir.
                              </small>
                            </div>
                          </div>

                          <hr className="my-24 my-lg-32" />

                          <div className="row gx-3 gy-3">
                            <div className="col-12">
                              <label
                                htmlFor="guestAddress"
                                className="form-label form-label-lg"
                              >
                                Adres Satırı{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <textarea
                                className="form-control form-control-lg"
                                id="guestAddress"
                                name="address"
                                value={guestInfo.address}
                                onChange={handleGuestInfoChange}
                                rows="3"
                                placeholder="Mahalle, cadde, sokak, bina ve daire no"
                                required
                              ></textarea>
                              <div className="invalid-feedback">
                                Lütfen adresinizi giriniz.
                              </div>
                            </div>
                            <div className="col-md-4">
                              <label
                                htmlFor="guestCity"
                                className="form-label form-label-lg"
                              >
                                Şehir <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-lg"
                                id="guestCity"
                                name="city"
                                value={guestInfo.city}
                                onChange={handleGuestInfoChange}
                                required
                              />
                              <div className="invalid-feedback">
                                Lütfen şehir bilginizi giriniz.
                              </div>
                            </div>
                            <div className="col-md-4">
                              <label
                                htmlFor="guestDistrict"
                                className="form-label form-label-lg"
                              >
                                İlçe <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-lg"
                                id="guestDistrict"
                                name="district"
                                value={guestInfo.district}
                                onChange={handleGuestInfoChange}
                                required
                              />
                              <div className="invalid-feedback">
                                Lütfen ilçe bilginizi giriniz.
                              </div>
                            </div>
                            <div className="col-md-4">
                              <label
                                htmlFor="guestPostCode"
                                className="form-label form-label-lg"
                              >
                                Posta Kodu
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-lg"
                                id="guestPostCode"
                                name="postCode"
                                value={guestInfo.postCode}
                                onChange={handleGuestInfoChange}
                              />
                            </div>
                          </div>

                          <div className="form-check mt-24 mb-20">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="guestBillingCheck"
                              checked={guestWantsDifferentBilling}
                              onChange={handleGuestBillingCheckChange}
                              style={{ width: "1.2em", height: "1.2em" }} // Checkbox boyutunu biraz büyütüyoruz
                            />
                            <label
                              className="form-check-label ms-2"
                              htmlFor="guestBillingCheck"
                              style={{ fontSize: "1em" }}
                            >
                              Fatura bilgilerim teslimat bilgilerimden farklı.
                            </label>
                          </div>

                          {guestWantsDifferentBilling && (
                            <div className="guest-billing-address border p-20 rounded-3 mt-0 mb-24 bg-light">
                              <h6 className="mb-20 fw-medium text-gray-700">
                                Fatura Bilgileri
                              </h6>
                              <div className="row gx-3 gy-3">
                                <div className="col-md-6">
                                  <label
                                    htmlFor="guestBillingFirstName"
                                    className="form-label form-label-lg"
                                  >
                                    Ad <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control form-control-lg"
                                    id="guestBillingFirstName"
                                    name="billingFirstName"
                                    value={guestInfo.billingFirstName}
                                    onChange={handleGuestInfoChange}
                                    required
                                  />
                                  <div className="invalid-feedback">
                                    Lütfen fatura için adınızı giriniz.
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <label
                                    htmlFor="guestBillingLastName"
                                    className="form-label form-label-lg"
                                  >
                                    Soyad <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control form-control-lg"
                                    id="guestBillingLastName"
                                    name="billingLastName"
                                    value={guestInfo.billingLastName}
                                    onChange={handleGuestInfoChange}
                                    required
                                  />
                                  <div className="invalid-feedback">
                                    Lütfen fatura için soyadınızı giriniz.
                                  </div>
                                </div>
                                <div className="col-12">
                                  <label
                                    htmlFor="guestBillingAddress"
                                    className="form-label form-label-lg"
                                  >
                                    Adres Satırı{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <textarea
                                    className="form-control form-control-lg"
                                    id="guestBillingAddress"
                                    name="billingAddress"
                                    value={guestInfo.billingAddress}
                                    onChange={handleGuestInfoChange}
                                    rows="3"
                                    required
                                  ></textarea>
                                  <div className="invalid-feedback">
                                    Lütfen fatura için adresinizi giriniz.
                                  </div>
                                </div>
                                <div className="col-md-4">
                                  <label
                                    htmlFor="guestBillingCity"
                                    className="form-label form-label-lg"
                                  >
                                    Şehir <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control form-control-lg"
                                    id="guestBillingCity"
                                    name="billingCity"
                                    value={guestInfo.billingCity}
                                    onChange={handleGuestInfoChange}
                                    required
                                  />
                                  <div className="invalid-feedback">
                                    Lütfen fatura için şehir bilginizi giriniz.
                                  </div>
                                </div>
                                <div className="col-md-4">
                                  <label
                                    htmlFor="guestBillingDistrict"
                                    className="form-label form-label-lg"
                                  >
                                    İlçe <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control form-control-lg"
                                    id="guestBillingDistrict"
                                    name="billingDistrict"
                                    value={guestInfo.billingDistrict}
                                    onChange={handleGuestInfoChange}
                                    required
                                  />
                                  <div className="invalid-feedback">
                                    Lütfen fatura için ilçe bilginizi giriniz.
                                  </div>
                                </div>
                                <div className="col-md-4">
                                  <label
                                    htmlFor="guestBillingPostCode"
                                    className="form-label form-label-lg"
                                  >
                                    Posta Kodu
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control form-control-lg"
                                    id="guestBillingPostCode"
                                    name="billingPostCode"
                                    value={guestInfo.billingPostCode}
                                    onChange={handleGuestInfoChange}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sipariş Notu Bölümü */}
            <div className="common-accordion mb-4">
              <div className="accordion-item rounded-8 shadow-sm border">
                <div className="accordion-header">
                  <div
                    className={`accordion-button ${
                      !accordionStates.orderNote ? "collapsed" : ""
                    }`}
                    onClick={() => toggleAccordion("orderNote")}
                    onMouseEnter={() => handleMouseEnter("orderNote")}
                    onMouseLeave={() => handleMouseLeave("orderNote")}
                    aria-expanded={accordionStates.orderNote}
                    style={getHeaderStyle("orderNote")}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle bg-success d-flex align-items-center justify-content-center me-2"
                          style={{
                            width: "24px",
                            height: "24px",
                            color: "white",
                            fontSize: "14px",
                            minWidth: "24px",
                          }}
                        >
                          3
                        </div>
                        <span className="ms-2">Sipariş Notu</span>
                      </div>
                      <div className="accordion-icon">
                        {accordionStates.orderNote ? (
                          <FaChevronUp
                            className={
                              hoverStates.orderNote
                                ? "text-success"
                                : "text-muted"
                            }
                          />
                        ) : (
                          <FaChevronDown
                            className={
                              hoverStates.orderNote
                                ? "text-success"
                                : "text-muted"
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* İçerik */}
                <div
                  className={`accordion-collapse collapse ${
                    accordionStates.orderNote ? "show" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <textarea
                      value={orderNotes}
                      onChange={(e) => setOrderNotes(e.target.value)}
                      className="form-control"
                      placeholder="Siparişinizle ilgili notlar, örneğin teslimat için özel notlar."
                      rows="4"
                      style={{ resize: "vertical", minHeight: "100px" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Ödeme Seçenekleri Bölümü */}
            <div className="common-accordion mb-4">
              <div className="accordion-item rounded-8 shadow-sm border">
                <div className="accordion-header">
                  <div
                    className={`accordion-button ${
                      !accordionStates.payment ? "collapsed" : ""
                    }`}
                    onClick={() => toggleAccordion("payment")}
                    onMouseEnter={() => handleMouseEnter("payment")}
                    onMouseLeave={() => handleMouseLeave("payment")}
                    aria-expanded={accordionStates.payment}
                    style={getHeaderStyle("payment")}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle bg-success d-flex align-items-center justify-content-center me-2"
                          style={{
                            width: "24px",
                            height: "24px",
                            color: "white",
                            fontSize: "14px",
                            minWidth: "24px",
                          }}
                        >
                          4
                        </div>
                        <span className="ms-2">Ödeme Seçenekleri</span>
                      </div>
                      <div className="accordion-icon">
                        {accordionStates.payment ? (
                          <FaChevronUp
                            className={
                              hoverStates.payment
                                ? "text-success"
                                : "text-muted"
                            }
                          />
                        ) : (
                          <FaChevronDown
                            className={
                              hoverStates.payment
                                ? "text-success"
                                : "text-muted"
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* İçerik */}
                <div
                  className={`accordion-collapse collapse ${
                    accordionStates.payment ? "show" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <div className="payment-methods">
                      {/* Ödeme Seçenekleri Kartları */}
                      <div className="payment-cards mb-4">
                        <div className="row gx-3">
                          {/* Kredi Kartı Seçeneği */}
                          <div className="col-md-4 mb-3 mb-md-0">
                            <div
                              className={`payment-card card h-100 border cursor-pointer`}
                              style={{
                                borderRadius: "12px",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                                overflow: "hidden",
                                boxShadow:
                                  selectedPayment === PaymentMethod.CREDIT_CARD
                                    ? "0 0 0 1px rgba(55, 178, 77, 0.5)"
                                    : "none",
                              }}
                              onClick={() =>
                                handlePaymentChange(PaymentMethod.CREDIT_CARD)
                              }
                            >
                              <div className="card-body p-8">
                                <div className="d-flex justify-content-center align-items-center">
                                  <div
                                    className="d-flex align-items-center justify-content-center me-3"
                                    style={{
                                      width: "48px",
                                      height: "48px",
                                      backgroundColor: "rgba(55, 178, 77, 0.1)",
                                      borderRadius: "8px",
                                    }}
                                  >
                                    <FaCreditCard className="fs-4 text-success" />
                                  </div>
                                  <div className="flex-grow-1">
                                    <h6 className="fw-semibold mb-1">
                                      Kredi Kartı ile Ödeme
                                    </h6>
                                    <p className="mb-0 small text-muted">
                                      Güvenli ödeme ile tek çekim
                                    </p>
                                  </div>
                                  <div className="ms-auto">
                                    <div
                                      className={`rounded-circle border ${
                                        selectedPayment ===
                                        PaymentMethod.CREDIT_CARD
                                          ? "border-success"
                                          : "border-secondary"
                                      }`}
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        padding: "3px",
                                      }}
                                    >
                                      {selectedPayment ===
                                        PaymentMethod.CREDIT_CARD && (
                                        <div className="bg-success rounded-circle w-100 h-100"></div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Havale/EFT Seçeneği */}
                          {/* <div className="col-md-4 mb-3 mb-md-0">
                                                        <div 
                                                            className={`payment-card card h-100 border cursor-pointer`}
                                                            style={{
                                                                borderRadius: "12px",
                                                                transition: "all 0.3s ease",
                                                                cursor: "pointer",
                                                                overflow: "hidden",
                                                                boxShadow: selectedPayment === PaymentMethod.BANK_TRANSFER ? '0 0 0 1px rgba(55, 178, 77, 0.5)' : 'none'
                                                            }}
                                                            onClick={() => handlePaymentChange(PaymentMethod.BANK_TRANSFER)}
                                                        >
                                                            <div className="card-body p-8">
                                                                <div className="d-flex justify-content-center align-items-center">
                                                                    <div className="d-flex align-items-center justify-content-center me-3" style={{width: "48px", height: "48px", backgroundColor: "rgba(55, 178, 77, 0.1)", borderRadius: "8px"}}>
                                                                        <FaUniversity className="fs-4 text-success" />
                                                                    </div>
                                                                    <div className="flex-grow-1">
                                                                        <h6 className="fw-semibold mb-1">Havale/EFT ile Ödeme</h6>
                                                                        <p className="mb-0 small text-muted">Banka hesabına ödeme yap</p>
                                                                    </div>
                                                                    <div className="ms-auto">
                                                                        <div className={`rounded-circle border ${selectedPayment === PaymentMethod.BANK_TRANSFER ? 'border-success' : 'border-secondary'}`} style={{width: "20px", height: "20px", padding: "3px"}}>
                                                                            {selectedPayment === PaymentMethod.BANK_TRANSFER && (
                                                                                <div className="bg-success rounded-circle w-100 h-100"></div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div> */}

                          {/* Kapıda Ödeme Seçeneği */}
                          {/* <div className="col-md-4">
                                                        <div 
                                                            className={`payment-card card h-100 border cursor-pointer}`}
                                                            style={{
                                                                borderRadius: "12px",
                                                                transition: "all 0.3s ease",
                                                                cursor: "pointer",
                                                                overflow: "hidden",
                                                                boxShadow: selectedPayment === PaymentMethod.CASH_ON_DELIVERY ? '0 0 0 1px rgba(55, 178, 77, 0.5)' : 'none'
                                                            }}
                                                            onClick={() => handlePaymentChange(PaymentMethod.CASH_ON_DELIVERY)}
                                                        >
                                                            <div className="card-body p-8">
                                                                <div className="d-flex justify-content-center align-items-center">
                                                                    <div className="d-flex align-items-center justify-content-center me-3" style={{width: "48px", height: "48px", backgroundColor: "rgba(55, 178, 77, 0.1)", borderRadius: "8px"}}>
                                                                        <FaMoneyBillWave className="fs-4 text-success" />
                                                                    </div>
                                                                    <div className="flex-grow-1">
                                                                        <h6 className="fw-semibold mb-1">Kapıda Ödeme</h6>
                                                                        <p className="mb-0 small text-muted">Teslimat anında nakit öde</p>
                                                                    </div>
                                                                    <div className="ms-auto">
                                                                        <div className={`rounded-circle border ${selectedPayment === PaymentMethod.CASH_ON_DELIVERY ? 'border-success' : 'border-secondary'}`} style={{width: "20px", height: "20px", padding: "3px"}}>
                                                                            {selectedPayment === PaymentMethod.CASH_ON_DELIVERY && (
                                                                                <div className="bg-success rounded-circle w-100 h-100"></div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div> */}
                        </div>
                      </div>

                      {/* Seçilen ödeme yöntemine göre detaylar */}
                      <div className="payment-details mt-20">
                        {selectedPayment === PaymentMethod.BANK_TRANSFER && (
                          <div className="payment-item__content py-4 px-4 bg-light rounded-8 d-block position-relative">
                            <p className="text-muted mb-3">
                              Ödemenizi doğrudan banka hesabımıza
                              yapabilirsiniz. Lütfen ödeme referansı olarak
                              Sipariş ID'nizi kullanın. Fonlar hesabımıza geçene
                              kadar siparişiniz gönderilmeyecektir.
                            </p>
                            <div className="bank-accounts mt-4">
                              <h6 className="fw-semibold mb-3">
                                Banka Hesap Bilgilerimiz
                              </h6>
                              <div className="card mb-2">
                                <div className="card-body py-3">
                                  <div className="d-flex align-items-center">
                                    <img
                                      src="/assets/images/banks/ziraat.png"
                                      alt="Ziraat Bankası"
                                      className="me-3"
                                      style={{ width: "40px" }}
                                    />
                                    <div>
                                      <h6 className="mb-0">Ziraat Bankası</h6>
                                      <p className="mb-0 small">
                                        IBAN: TR12 0006 4000 0012 3456 7890 12
                                      </p>
                                      <p className="mb-0 small">
                                        Hesap Sahibi: Gümüş Pestil Ltd. Şti.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="card mb-2">
                                <div className="card-body py-3">
                                  <div className="d-flex align-items-center">
                                    <img
                                      src="/assets/images/banks/isbank.png"
                                      alt="İş Bankası"
                                      className="me-3"
                                      style={{ width: "40px" }}
                                    />
                                    <div>
                                      <h6 className="mb-0">İş Bankası</h6>
                                      <p className="mb-0 small">
                                        IBAN: TR98 7654 3210 0123 4567 8901 23
                                      </p>
                                      <p className="mb-0 small">
                                        Hesap Sahibi: Gümüş Pestil Ltd. Şti.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <p className="text-muted mt-4 mb-0 small fw-semibold">
                              <i className="fas fa-info-circle me-1"></i> Ödeme
                              yaptıktan sonra, bizi arayarak veya e-posta
                              göndererek ödemenizi bildirmeniz işleminizi
                              hızlandıracaktır.
                            </p>
                          </div>
                        )}

                        {selectedPayment === PaymentMethod.CASH_ON_DELIVERY && (
                          <div className="payment-item__content py-4 px-4 bg-light rounded-8 d-block position-relative">
                            <p className="text-muted mb-0">
                              Teslimat anında ödeme yapacaksınız. Kurye teslimat
                              sırasında nakit ödeme kabul edecektir. Lütfen tam
                              ücreti hazır bulundurun.
                            </p>
                            <div className="alert alert-warning mt-3 mb-0">
                              <i className="fas fa-exclamation-triangle me-2"></i>{" "}
                              Kapıda ödeme seçeneğinde +20 ₺ hizmet bedeli
                              uygulanmaktadır.
                            </div>
                          </div>
                        )}

                        {selectedPayment === PaymentMethod.CREDIT_CARD && (
                          <div className="py-4 px-4  d-block position-relative">
                            <div className="row bg-light rounded-8 payment-item__content ">
                              <div className="col-lg-12">
                                <div className="credit-card-form">
                                  <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <label
                                        htmlFor="cardNumber"
                                        className="form-label mb-0 fw-medium"
                                      >
                                        Kart Numarası
                                      </label>
                                    </div>
                                    <input
                                      type="text"
                                      className="form-control form-control-lg"
                                      id="cardNumber"
                                      name="cardNumber"
                                      placeholder="0000 0000 0000 0000"
                                      value={cardInfo.cardNumber}
                                      onChange={handleCardInfoChange}
                                      autoComplete="cc-number"
                                      inputMode="numeric"
                                    />
                                  </div>
                                  <div className="mb-4">
                                    <label
                                      htmlFor="cardHolderName"
                                      className="form-label mb-1 fw-medium"
                                    >
                                      Kart Üzerindeki İsim
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control form-control-lg"
                                      id="cardHolderName"
                                      name="cardHolderName"
                                      placeholder="AHMET YILMAZ"
                                      value={cardInfo.cardHolderName}
                                      onChange={handleCardInfoChange}
                                    />
                                  </div>
                                  <div className="row">
                                    <div className="col-md-8 mb-4">
                                      <label className="form-label mb-1 fw-medium">
                                        Son Kullanma Tarihi
                                      </label>
                                      <div className="d-flex">
                                        <select
                                          className="form-select form-select-lg me-20"
                                          name="expiryMonth"
                                          value={cardInfo.expiryMonth}
                                          onChange={handleCardInfoChange}
                                        >
                                          <option value="">Ay</option>
                                          {Array.from(
                                            { length: 12 },
                                            (_, i) => (
                                              <option key={i} value={i + 1}>
                                                {String(i + 1).padStart(2, "0")}
                                              </option>
                                            )
                                          )}
                                        </select>
                                        <select
                                          className="form-select form-select-lg"
                                          name="expiryYear"
                                          value={cardInfo.expiryYear}
                                          onChange={handleCardInfoChange}
                                        >
                                          <option value="">Yıl</option>
                                          {Array.from(
                                            { length: 10 },
                                            (_, i) => {
                                              const year =
                                                new Date().getFullYear() + i;
                                              return (
                                                <option key={year} value={year}>
                                                  {year}
                                                </option>
                                              );
                                            }
                                          )}
                                        </select>
                                      </div>
                                    </div>
                                    <div className="col-md-4 mb-4">
                                      <label
                                        htmlFor="cvv"
                                        className="form-label mb-1 fw-medium"
                                      >
                                        CVV
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control form-control-lg"
                                        id="cvv"
                                        name="cvv"
                                        placeholder="123"
                                        value={cardInfo.cvv}
                                        onChange={handleCardInfoChange}
                                        autoComplete="cc-csc"
                                        inputMode="numeric"
                                      />
                                    </div>
                                  </div>
                                  <div className="mb-0">
                                    <div className="card-security-info">
                                      <FaLock className="icon" />
                                      <p className="mb-0 small text-muted">
                                        Ödeme bilgileriniz 256-bit SSL ile
                                        güvenle şifrelenmektedir.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/* Taksit seçenekleri geçici olarak gizlendi */}
                              {/* <div className="col-lg-4 mt-4 mt-lg-0">
                                <div className="installment-options bg-white p-20 rounded-8 border h-100">
                                  <h6 className="fw-semibold mb-4">
                                    Taksit Seçenekleri
                                  </h6>
                                  {fetchingInstallments ? (
                                    <div className="text-center py-3">
                                      <LoadingSpinner 
                                        size="sm" 
                                        color="primary" 
                                        text="Taksit seçenekleri yükleniyor..."
                                      />
                                    </div>
                                  ) : installmentOptions.length > 0 ? (
                                    // Taksit seçenekleri varsa
                                    <div className="installment-options-list">
                                      {installmentOptions.map((option) => (
                                        <div
                                          className="installment-option-item mb-3"
                                          key={option.installment}
                                        >
                                          <label className="d-flex align-items-center w-100 cursor-pointer">
                                            <div className="custom-radio me-2">
                                              <input
                                                type="radio"
                                                name="installmentOption"
                                                checked={
                                                  selectedInstallment ===
                                                  option.installment
                                                }
                                                onChange={() =>
                                                  handleInstallmentChange(
                                                    option.installment
                                                  )
                                                }
                                                className="visually-hidden"
                                              />
                                              <span className="radio-indicator"></span>
                                            </div>
                                            <div className="d-flex justify-content-between w-100">
                                              <span>
                                                {option.installment === 1
                                                  ? "Tek Çekim"
                                                  : `${option.installment} Taksit`}
                                              </span>
                                              <span className="fw-semibold text-success">
                                                {option.installment === 1
                                                  ? `${(typeof rawTotalPrice === // rawTotalPrice (getCartTotalPrice) tek çekim için kullanılır
                                                    "number"
                                                      ? rawTotalPrice
                                                      : 0
                                                    ).toFixed(2)} TL`
                                                  : option.amount != null
                                                  ? `${option.amount.toFixed(
                                                      // Aylık taksit tutarı
                                                      2
                                                    )} TL x ${
                                                      // "x Taksit Sayısı" kaldırıldı, sadece aylık tutar gösteriliyor
                                                      option.installment
                                                    }`
                                                  : "N/A"}
                                              </span>
                                            </div>
                                          </label>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="installment-option-item mb-3">
                                      <label className="d-flex align-items-center w-100 cursor-pointer">
                                        <div className="custom-radio me-2">
                                          <input
                                            type="radio"
                                            name="installmentOption"
                                            checked={true}
                                            readOnly
                                            className="visually-hidden"
                                          />
                                          <span className="radio-indicator"></span>
                                        </div>
                                        <div className="d-flex justify-content-between w-100">
                                          <span>Tek Çekim</span>
                                          <span className="fw-semibold text-success">
                                            {(typeof rawTotalPrice === // rawTotalPrice kullanılır
                                            "number"
                                              ? rawTotalPrice
                                              : 0
                                            ).toFixed(2)}{" "}
                                            TL
                                          </span>
                                        </div>
                                      </label>
                                    </div>
                                  )}
                                </div>
                              </div> */}
                            </div>

                            {/* Ödeme Logoları - Kredi kartı bölümünün içinde, gri alanın dışında */}
                            <div className="mt-20">
                              <div className="d-block d-md-flex justify-content-end align-items-center">
                                <img
                                  src="/assets/images/brands/tekparca-logolar-2.jpg"
                                  alt="Ödeme Seçenekleri"
                                  className="payment-logos"
                                  style={{
                                    maxHeight: "50px",
                                    objectFit: "contain",
                                    marginRight: "15px",
                                  }}
                                />
                                <img
                                  src="/assets/images/brands/tekparca-logolar-1.jpg"
                                  alt="Ödeme Seçenekleri"
                                  className="payment-logos"
                                  style={{
                                    maxHeight: "50px",
                                    objectFit: "contain",
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sözleşmeler Bölümü */}
            <div className="common-accordion mb-4">
              <div className="accordion-item rounded-8 shadow-sm border">
                <div className="accordion-header">
                  <div
                    className={`accordion-button ${
                      !accordionStates.terms ? "collapsed" : ""
                    }`}
                    onClick={() => toggleAccordion("terms")}
                    onMouseEnter={() => handleMouseEnter("terms")}
                    onMouseLeave={() => handleMouseLeave("terms")}
                    aria-expanded={accordionStates.terms}
                    style={getHeaderStyle("terms")}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle bg-success d-flex align-items-center justify-content-center me-2"
                          style={{
                            width: "24px",
                            height: "24px",
                            color: "white",
                            fontSize: "14px",
                            minWidth: "24px",
                          }}
                        >
                          5
                        </div>
                        <span className="ms-2">Sözleşmeler</span>
                      </div>
                      <div className="accordion-icon">
                        {accordionStates.terms ? (
                          <FaChevronUp
                            className={
                              hoverStates.terms ? "text-success" : "text-muted"
                            }
                          />
                        ) : (
                          <FaChevronDown
                            className={
                              hoverStates.terms ? "text-success" : "text-muted"
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* İçerik */}
                <div
                  className={`accordion-collapse collapse ${
                    accordionStates.terms ? "show" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="termsCheck"
                          checked={agreedToTerms}
                          onChange={(e) => setAgreedToTerms(e.target.checked)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="termsCheck"
                        >
                          <span className="fw-semibold">
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openTermsModal("mesafeliSatis");
                              }}
                              className="text-success text-decoration-underline"
                            >
                              Mesafeli Satış Sözleşmesi
                            </a>
                            {" ve "}
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openTermsModal("kvkk");
                              }}
                              className="text-success text-decoration-underline"
                            >
                              KVKK Metni
                            </a>
                          </span>
                          'ni okudum, kabul ediyorum.
                        </label>
                      </div>
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-link text-success p-0 text-decoration-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowTerms(!showTerms);
                        }}
                      >
                        <i
                          className={`fas fa-chevron-${
                            showTerms ? "up" : "down"
                          } me-1 small`}
                        ></i>
                        {showTerms
                          ? "Sözleşmeleri Gizle"
                          : "Sözleşmeleri Göster"}
                      </button>

                      {showTerms && (
                        <div className="mt-3 p-3 bg-light rounded-8">
                          <h6 className="fw-bold">Mesafeli Satış Sözleşmesi</h6>
                          <p className="text-muted small mb-4">
                            Burada Mesafeli Satış Sözleşmesi metni yer
                            alacaktır...
                          </p>

                          <h6 className="fw-bold">KVKK Metni</h6>
                          <p className="text-muted small mb-0">
                            Burada KVKK metni yer alacaktır...
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sağ sütun - Sipariş Özeti */}
          <div className="col-xl-3 col-lg-4">
            <div
              className="checkout-sidebar position-sticky"
              style={{ top: "120px" }}
            >
              <div className="card border-0 shadow-lg mb-4 rounded-lg">
                <div className="card-header bg-success text-white py-3 border-bottom rounded-top-lg">
                  <h5 className="card-title mb-0 fw-semibold">Sipariş Özeti</h5>
                </div>
                <div className="card-body p-0">
                  {/* Sepetinizdeki Ürünler bölümü kaldırıldı */}

                  <div className="px-3 py-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted fs-sm">
                        Ürünlerin Toplamı
                      </span>
                      <span className="fw-medium fs-sm">
                        {getCartTotalPrice().toFixed(2)} TL
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted fs-sm">Kargo Ücreti</span>
                      <span className="fw-medium text-success fs-sm">
                        {shippingCost > 0 ? `${shippingCost.toFixed(2)} TL` : 'Ücretsiz'}
                      </span>
                    </div>
                    {/* Taksit bilgileri geçici olarak gizlendi */}
                    {/* {selectedPayment === PaymentMethod.CREDIT_CARD &&
                      selectedInstallment > 1 &&
                      installmentOptions.length > 0 &&
                      (() => {
                        const currentInstallmentPlan = installmentOptions.find(
                          (opt) => opt.installment === selectedInstallment
                        );
                        if (
                          currentInstallmentPlan &&
                          currentInstallmentPlan.amount != null
                        ) {
                          return (
                            <div className="d-flex justify-content-between mb-2 text-info">
                              <span className="fs-sm">
                                Taksit ({selectedInstallment} Ay)
                              </span>
                              <span className="fw-medium fs-sm">
                                {selectedInstallment} x{" "}
                                {currentInstallmentPlan.amount.toFixed(2)} TL
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()} */}
                  </div>

                  <div className="border-top-dashed py-3 px-3 bg-light-subtle mb-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-bold text-dark">Genel Toplam</span>
                      <span className="fw-bold fs-5 text-success">
                        {(typeof finalDisplayPrice === "number"
                          ? finalDisplayPrice
                          : 0
                        ).toFixed(2)}{" "}
                        TL
                      </span>
                    </div>
                  </div>

                  <div className="p-3">
                    <button
                      onClick={handleSubmit}
                      disabled={isButtonDisabled}
                      className="btn btn-success w-100 py-20 btn-lg fw-semibold mb-3"
                      style={{
                        borderRadius: "8px",
                        fontSize: "1.05rem",
                        boxShadow: "0 4px 10px rgba(40, 167, 69, 0.3)",
                        opacity: isButtonDisabled ? 0.65 : 1,
                        cursor: isButtonDisabled ? "not-allowed" : "pointer",
                      }}
                    >
                      {loading ? (
                        <>
                          <LoadingSpinner size="sm" color="white" />
                          <span style={{ marginLeft: '8px' }}>İşleniyor...</span>
                        </>
                      ) : (
                        <>
                          <FaLock
                            className="me-2"
                            style={{ fontSize: "0.9em" }}
                          />
                          Güvenli Ödeme Yap
                        </>
                      )}
                    </button>

                    {/* Koşullu uyarı mesajları */}
                    {!loading &&
                      (!agreedToTerms || !isAddressDataSufficient) && (
                        <div className="mb-3">
                          {!agreedToTerms && (
                            <div className="text-center">
                              <small className="text-danger fw-medium">
                                Devam etmek için sözleşmeleri kabul etmelisiniz.
                              </small>
                            </div>
                          )}
                          {/* Adres yetersizse ve sözleşme onaylanmamışsa, adres uyarısı için biraz boşluk bırak */}
                          {!isAddressDataSufficient && (
                            <div
                              className={`text-center ${
                                !agreedToTerms ? "mt-1" : ""
                              }`}
                            >
                              <small className="text-danger fw-medium">
                                {
                                  isAuthenticated()
                                    ? "Devam etmek için bir teslimat adresi seçin."
                                    : isGuestCheckout
                                    ? "Devam etmek için tüm teslimat ve iletişim bilgilerinizi (geçerli TC Kimlik No dahil) eksiksiz doldurun."
                                    : "Devam etmek için adres bilgilerinizi girin/seçin." // Genel fallback mesajı
                                }
                              </small>
                            </div>
                          )}
                        </div>
                      )}

                    <div className="mt-2 border-top-dashed pt-3 text-center">
                      <div className="d-flex align-items-center justify-content-center text-muted">
                        <FaLock
                          className="text-success me-2"
                          style={{ fontSize: "0.9em" }}
                        />
                        <small style={{ fontSize: "0.75rem" }}>
                          Ödemeleriniz 256-bit SSL ile güvenle korunmaktadır.
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobil cihazlar için sabit sipariş özeti - Sadece xs ve sm ekranlarda görünür */}
          <div className="d-lg-none mobile-checkout-summary border-top shadow-lg">
            <div className="container py-2">
              <div className="row align-items-center gx-2">
                <div className="col">
                  <div className="d-flex flex-column text-start">
                    <span className="text-muted fs-xs">Toplam Tutar</span>
                    <span className="fw-bold text-success fs-5 lh-sm">
                      {(typeof finalDisplayPrice === "number"
                        ? finalDisplayPrice
                        : 0
                      ).toFixed(2)}{" "}
                      TL
                    </span>
                    {/* Taksit bilgisi geçici olarak gizlendi */}
                    {/* {selectedPayment === PaymentMethod.CREDIT_CARD &&
                      selectedInstallment > 1 && (
                        <small className="text-muted fs-xs lh-1">
                          {selectedInstallment} taksit
                        </small>
                      )} */}
                  </div>
                </div>
                <div className="col-auto" style={{ flexBasis: "150px" }}>
                  <button
                    onClick={handleSubmit}
                    disabled={isButtonDisabled}
                    className="btn btn-success w-100 py-18 fw-semibold"
                    style={{
                      fontSize: "0.9rem",
                      opacity: isButtonDisabled ? 0.65 : 1,
                      cursor: isButtonDisabled ? "not-allowed" : "pointer",
                    }}
                  >
                    {loading ? (
                      <LoadingSpinner size="sm" color="white" />
                    ) : (
                      <>
                        <FaLock
                          className="me-1"
                          style={{ fontSize: "0.8em" }}
                        />
                        Ödeme Yap
                      </>
                    )}
                  </button>
                </div>
              </div>
              {!loading && (!agreedToTerms || !isAddressDataSufficient) && (
                <div className="row mt-1">
                  <div className="col-12 text-center">
                    {!agreedToTerms && (
                      <small className="text-danger d-block fs-xs fw-medium">
                        Sözleşmeleri kabul edin.
                      </small>
                    )}
                    {!isAddressDataSufficient && (
                      <small
                        className={`text-danger d-block fs-xs fw-medium ${
                          !agreedToTerms ? "mt-1" : ""
                        }`}
                      >
                        {isAuthenticated()
                          ? "Adres seçin."
                          : isGuestCheckout
                          ? "Teslimat bilgilerini (TC No dahil) eksiksiz doldurun."
                          : "Adres bilgilerini girin/seçin."}
                      </small>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Adres Ekleme/Düzenleme Modalı */}
        <AddressModal
          show={showAddressModal}
          onHide={() => setShowAddressModal(false)}
          onSave={handleSaveAddress}
          existingAddress={selectedAddressForEdit}
          addressType={addressModalType}
        />

        {/* Sözleşme Modalı */}
        {/* PayTR redirect sistemi kullanıldığından modal kaldırıldı */}

        <CustomModal
          show={showTermsModal}
          onHide={() => setShowTermsModal(false)}
          title={termsModalContent.title}
          size="lg"
        >
          <div className="p-3 contract-container" style={{ maxWidth: "100%" }}>
            <div
              className="contract-content"
              dangerouslySetInnerHTML={{ __html: termsModalContent.content }}
            />
          </div>
        </CustomModal>

      </div>
    </section>
  );
};

export default Checkout;
